import React, { useState, useEffect, useCallback, useRef } from 'react';
import { StatNumber } from './StatNumber';
import { useRewardAudio } from '../hooks/useRewardAudio';
import { useSession } from '../contexts/SessionContext';
import { useAudioSettings } from '../contexts/AudioSettingsContext';
import { formatTime } from './HeaderWidgets';
import {
  CoffeeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  EditIcon,
  FocusIcon,
  FocusOffIcon,
  GameIcon,
  HelpIcon,
  MoonIcon,
  NotesIcon,
  PauseIcon,
  SignalIcon,
  SignalOffIcon,
  StopIcon,
  TargetIcon,
  ToolsIcon,
  ZapIcon,
} from './HeaderIcons';
import { buildHeaderStripMetrics } from '../utils/headerMetrics';
import { isLastCallValidToday, offCallGapSeconds } from '../utils/callTimers';
import { MicVerifyChip } from './MicVerifyChip';
import { HoldReadout } from './HoldReadout';
import { computeCatchUp, formatCatchUpLine, formatCatchUpVerdict } from '../utils/catchUpPlan';
import {
  buildOffCallStatusLabel,
} from '../utils/offCallIdleMessages';
import { dispatchOpenDeepgramSettings } from '../utils/deepgramSettingsPrompt';
import { daysPerWeekOf } from './DialGoalSelector';
import { perWorkdayFromStats, weeklyHoursFromCommitment, savedGoalWorkDays } from '../utils/goalAnchor';
import { fmtHm } from '../utils/catchUpPlan';
import { ElementHintTarget, useElementHint, buildHintPayload } from './ElementHint';
import { useProgressiveAudio } from '../hooks/useProgressiveAudio';
import { TimeEditModal } from './TimeEditModal';
import { GameScoreboard } from './GameScoreboard';
import { AppGuideButton } from './AppGuide';
import { SettingsButton } from './SettingsButton';
import { WorkspaceViewSwitcher } from './WorkspaceViewSwitcher';
import { ConnectInterpretButton } from './ConnectInterpretButton';
import { AudioRouteStatusBar } from './AudioRouteStatusBar';
import { HeaderMetricsStrip } from './HeaderMetricsStrip';
import { playTestToneLocal, playTestToneSink } from '../utils/audioSelfTest';
import { displayDeviceName } from '../utils/audioDeviceLabels';
import { APP_VERSION_LABEL } from '../constants/version';
import { AutopilotChip } from './AutopilotGuard';
import { getAppStatus } from '../utils/appStatus';
import { SlotMicroValue } from './SlotMicroValue';
import { needsUserSuppliedDeepgramKey } from '../utils/deepgramRuntimeKey';
import { resolveConnectIntent } from '../utils/connectEvidence';
import {
  isComponentVisible,
  shouldShowProgressStack,
  useComponentVisibilityRefresh,
} from '../utils/componentVisibility';
import {
  getPresetConfig,
  PRESET_LABELS,
} from '../utils/scoreboardLayout';
import {
  loadLanguagePair,
  formatPairShort,
  LANG_PAIR_CHANGED_EVENT,
} from '../utils/languageConfig';
import { DailyTargetsChip, computeGoalDay } from './DailyTargetsChip';
import { loadHeaderToggle, saveHeaderToggle, shouldShowIoStrip } from '../utils/headerToggles';

const OFF_CALL_METRICS_EXPANDED_KEY = 'catint_off_call_metrics_expanded_v1';
const SCOREBOARD_MAX_VH_KEY = 'catint_scoreboard_max_vh';
const CelebrationParticles = ({ type, label, coins, onDismiss }) => {
  const [isClosing, setIsClosing] = useState(false);
  const emojis = ['🪙', '🪙', '💸', '💵', '💰', '💎'];
  const spread = type === 'month' ? 800 : (type === 'day' ? 600 : 350);
  const originX = type === 'month' ? '0px' : '-185px';
  // Cap particles. Celebration audio is fired once by its explicit event
  // handler; never tie sound to this rendering animation.
  const safeCoinCount = Math.min(60, coins);
  
  const handleDismiss = () => {
    setIsClosing(true);
    setTimeout(() => onDismiss(), 250);
  };

  return (
    <div style={{ position: 'absolute', inset: -50, pointerEvents: 'auto', cursor: 'pointer', zIndex: 100, animation: isClosing ? 'fadeOutFast 0.25s forwards' : 'none' }} onClick={handleDismiss}>
      {Array.from({ length: safeCoinCount }).map((_, i) => (
        <span key={i} style={{
          position: 'absolute', left: '50%', top: '50%', fontSize: `${0.8 + Math.random() * 1.2}rem`,
          animation: `coinVacuum 1.8s cubic-bezier(0.25, 1, 0.5, 1) forwards`,
          animationDelay: `${Math.random() * 0.4}s`,
          '--origin-x': originX, '--origin-y': '0px',
          '--start-x': `calc(${originX} + ${(Math.random() - 0.5) * spread * 1.5}px)`, 
          '--start-y': `${-Math.random() * (spread * 1.2)}px`,
          filter: `drop-shadow(0 0 ${2 + Math.random() * 8}px gold)`,
          zIndex: Math.floor(Math.random() * 10)
        }}>{emojis[Math.floor(Math.random() * emojis.length)]}</span>
      ))}
      <div style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          fontSize: '1.4rem', fontWeight: 900,
          color: type === 'day' || type === 'month' ? '#fcd34d' : '#6ee7b7',
          textShadow: `0 0 30px ${type === 'day' ? '#f59e0b' : '#10b981'}`,
          whiteSpace: 'nowrap', animation: `textFloatTarget 2.5s ease-out forwards`,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          zIndex: 100
      }}>
        {label.includes('AR$') ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
            <span>{label.split('AR$')[0]}</span>
            <StatNumber value={label.split('AR$')[1].replace(/[^\d]/g, '')} prefix="AR$" size="lg" format={false} />
          </div>
        ) : label}
        <div style={{ fontSize: '0.5rem', fontWeight: 400, color: 'rgba(255,255,255,0.7)', textShadow: 'none', marginTop: '0.2rem' }}>[Click to Skip]</div>
      </div>
    </div>
  );
};

/* HUD tidy B: single source for the ON-call live timer (expanded + compact
   micro-bar mounted the same markup twice) and for the targets chip
   (3 exclusive branches: in-call, off-call, meter-only). */
const MicroBarTimer = ({ showConnecting, connectionMessage, sessionSeconds }) => (
  <span className="call-micro-bar-slot call-micro-bar-timer" title={`ON CALL live — ${formatTime(sessionSeconds)}`}>
    {showConnecting ? (
      <span style={{ fontSize: '0.68rem', fontWeight: 900, color: '#f59e0b' }}>
        {connectionMessage || 'Connecting…'}
      </span>
    ) : (
      <span aria-label={`On call live ${formatTime(sessionSeconds)}`}>
        <span aria-hidden="true">📞 </span>
        <SlotMicroValue text={formatTime(sessionSeconds)} />
      </span>
    )}
  </span>
);

const StickyTargetsChip = ({ dailyMinutes, monthlyMinutes, workDays, breakMinutes, ratePerMinute, goalMinutes, goalSetAt, goalBaseMinutes, onOpenGoalsView }) => (
  <DailyTargetsChip dailyMinutes={dailyMinutes} monthlyMinutes={monthlyMinutes} workDays={workDays} breakMinutes={breakMinutes} ratePerMinute={ratePerMinute} goalMinutes={goalMinutes} goalSetAt={goalSetAt} goalBaseMinutes={goalBaseMinutes} onOpenGoalsView={onOpenGoalsView} />
);

/**
 * v4.164.0 — a scoreboard pill that shows a NUMBER and can be clicked to copy it.
 *
 * These pills have carried `cursor: copy` (index.css, .metric-pill) since long
 * before anything was listening, so the pointer promised a copy that never
 * happened. The operator's rule is "all numbers click to auto copy", so this
 * makes the promise true rather than removing the cursor.
 *
 * Keyboard reachable on purpose: these are divs, so they need role + tabIndex +
 * Enter/Space or they are mouse-only.
 */
const CopyPill = ({ id, title, copyText, copied, onCopy, style, children }) => {
  const value = String(copyText ?? '').trim();
  const activate = () => { if (value) onCopy(id, value); };
  return (
    <div
      id={id}
      className="metric-pill compact-pill is-copyable"
      role="button"
      tabIndex={0}
      aria-label={`${title || id}: ${value}. Click to copy.`}
      title={`${title || id} — click to copy`}
      onClick={activate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(); }
      }}
      style={style}
    >
      {children}
      {copied === id && <span className="pill-copied-flag" aria-hidden="true">✓</span>}
    </div>
  );
};

const StateIndicators = ({ state, breakMinutes, isZombie, silenceCount }) => {  const showSilenceTimer = silenceCount > 30;
  if (state === 'call') {
    return (
      <div className="emoji-money" style={{ fontSize: '1.1rem', marginRight: '0.2rem' }}>💰</div>
    );
  }
  if (state === 'break') {
    // 90 mins total budget. Each cup = 10 mins approx (total 9 cups)
    const cups = 9;
    const spentCups = Math.min(cups, Math.floor(breakMinutes / 10));
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <div className="resource-drain" title={`Break Budget: ${Math.floor(breakMinutes)}/90m used`} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px' }}>
          {Array.from({ length: cups }).map((_, i) => (
            <span key={i} className={`resource-item ${i < spentCups ? 'spent' : ''}`} style={{ fontSize: '0.85rem', lineHeight: 1 }}>
              {i < spentCups ? '🍵' : '☕'}
            </span>
          ))}
        </div>
        <span
          style={{
            fontSize: '0.65rem',
            color: '#fb923c',
            fontWeight: 600,
            fontFamily: 'var(--font-mono)',
            minWidth: '5.6ch',
            visibility: showSilenceTimer ? 'visible' : 'hidden',
            display: 'inline-block',
            textAlign: 'center',
          }}
        >
          {formatTime(silenceCount)}
        </span>
      </div>
    );
  }
  // Zombie
  if (isZombie) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <div style={{ animation: 'pulseWarning 1s infinite', fontSize: '1rem', color: '#f59e0b' }}>🤖</div>
        <span
          style={{
            fontSize: '0.65rem',
            color: '#f59e0b',
            fontWeight: 800,
            fontFamily: 'var(--font-mono)',
            minWidth: '5.6ch',
            visibility: showSilenceTimer ? 'visible' : 'hidden',
            display: 'inline-block',
            textAlign: 'center',
          }}
        >
          {formatTime(silenceCount)}
        </span>
      </div>
    );
  }
  // Avail
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
      <div style={{ animation: 'encouragePulse 3s infinite', fontSize: '1rem', color: '#fb923c' }}>
        {Math.floor(Date.now() / 2000) % 2 === 0 ? '📡' : '⏳'}
      </div>
      <span
        style={{
          fontSize: '0.75rem',
          color: '#fb923c',
          fontWeight: 800,
          fontFamily: 'var(--font-mono)',
          background: 'rgba(251, 146, 60, 0.1)',
          padding: '0 4px',
          borderRadius: '4px',
          minWidth: '6.6ch',
          visibility: showSilenceTimer ? 'visible' : 'hidden',
          display: 'inline-block',
          textAlign: 'center',
        }}
      >
        {formatTime(silenceCount)}
      </span>
    </div>
  );
};

const SessionControlsSticky = React.memo(({
  isActive,
  // v4.176.0: header show/hide toggles (src/utils/headerToggles.js)
  ioStripOn = true,
  onToggleIoStrip,
  gridInCall = false,
  onToggleGridInCall,
  isBreakActive,
  isZombieCall,
  apiKeyMissing,
  vaultNeedsDecrypt,
  apiKeyMissingNoVault = false,
  audioAttached,
  micTestMode,
  setMicTestMode,
  connectionState,
  connectionMessage,
  connectProgress,
  apiKeyRejected,
  showEndDayButton,
  onEndDay,

  // Connect UX
  connectFlash,
  connectLabel,
  connectSingleTitle,
  connectDoubleTitle,
  connectOnSingle,
  connectOnDouble,
  requireDoubleTapIndicator,
  pendingDoubleTapTitle,
  onArmDoubleTap,

  settingsOpen = false,
  vaultStatus = 'idle',

  // Break
  minutesSinceLastBreak,
  shouldBreakNudge,
  stopBreak,
  onStartBreak,

  // Call controls
  handleStop,
  isHold,
  setIsHold,
  holdSeconds = 0,
  disableZap,
  isZapping,
  onReconnectStream,
  sttStall = null,

  // Call micro bar
  silenceCount,
  lastEnglishActivityTime,
  sessionSeconds,
  sessionArsLive,
  totalOffCallSeconds = 0,
  totalOnCallSeconds = 0,
  // v4.124.0: last-call snapshot + live avail — powers OFF 🚪/📞LAST counters
  lastCallSeconds = 0,
  lastCallEndedAt = 0,
  availSeconds = 0,
  // v4.87.0: daily $1200-goal chip in status bar
  dailyMinutes = 0,
  monthlyMinutes = 0,
  workDays = 0, // v4.101.0: workday basis (e.g. 28 for 6.5/Wk) for the deficit math
  breakMinutes = 0,
  ratePerMinute = 0.13,
  goalMinutes = 0, // v4.96.0: dial goal for the chip's deficit pair + catch-up target
  goalSetAt = null, goalBaseMinutes = 0, // ANCHORED-GOAL: when it was banked + base
  // Today timers (minutes) — shown next to Connect in the sticky row (v4.86.5)
  onCallMins = 0,
  offCallMins = 0,
  // Daily income (ARS) — big orange counter in the sticky row (v4.87.0)
  dailyIncomeArs = 0,
  callModeExpanded,
  setCallModeExpanded,

  tabStreamReady = false,
  cableStreamReady = false,
  lastDataTime = 0,
  // v4.167.0: the Soundboard 🎛 and Greeting Editor ✎ buttons are gone from the
  // strip — they were the only doors to those views, and both now open from
  // Settings → Studio. These two props STAY because the cat logo below still
  // uses them to close the studio, which is a "back to work" affordance rather
  // than an entrance. onOpenGreetingEditor / greetingEditorOpen / onToggleLanguage
  // are gone entirely (the editor opened from Settings, EN|ES from Settings →
  // Language).
  onOpenSoundboard,
  soundboardOpen = false,
  // v4.113.0: HUD goal button opens the Goal Tracking VIEW (dial + calendar)
  onOpenGoalsView,
  goalsOpen = false,

  // Audio route UX props (passed into AudioRouteStatusBar)
  configuredAudioSourceMode = "tab",
  attachedAudioSourceMode = "tab",
  virtualCableFailure = null,
  onReconnectAudioSource,
  onSwitchToTabShare,
  onSwitchToVirtualCable,

  sttLanguage = 'auto',
  onToggleLanguage,
  onOpenLanguageSettings,
  languagePairLabel,
  offCallStatusLabel = 'Ready',
  // v4.88.5: 3rd HUD mode — meter-only (extra compact) during calls
  meterOnly = false,
  onToggleMeterHud,
  /** v4.99.0: node rendered at the right end of the audio chips row
      (off-call scoreboard merges its metrics strip into that single line). */
  trailingSlot = null,
}) => {
  const showConnecting = isActive && connectionState !== 'connected';
  const slackText = `SLACK ${formatTime(silenceCount)}`;
  // v4.124.0: OFF-call gap + LAST call counters. This sticky bar already
  // re-renders every second (sessionSeconds on-call, availSeconds off-call),
  // so Date.now() here ticks live with zero extra timers.
  const lastCallValidToday = isLastCallValidToday(lastCallEndedAt || 0);
  const offCallGapSecs = offCallGapSeconds({ isActive, lastCallEndedAt: lastCallEndedAt || 0 });
  const appStatus = getAppStatus({
    isActive,
    isZombieCall,
    apiKeyMissing,
    apiKeyRejected,
    connectionState,
    connectProgress,
    virtualCableFailure,
  });

  // v4.100.0: cat logo = "take me back to transcription".
  // v4.113.0: also exits the Goal Tracking view.
  // Exits Soundboard Studio if open, then scrolls the transcript into view.
  const handleLogoBack = () => {
    try {
      if (soundboardOpen && onOpenSoundboard) onOpenSoundboard();
    } catch (_) {}
    try {
      if (goalsOpen && onOpenGoalsView) onOpenGoalsView(); // toggle back to work view
    } catch (_) {}
    try {
      const el = document.getElementById('main-transcript') || document.querySelector('.transcription-pane');
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'start' });
    } catch (_) {}
  };

  // v4.165.0: the long-press (450ms) EN|ES handlers and their two label strings
  // went with the header button. The pair now changes only in Settings →
  // Language, which owns the real pickers.
  const showBreakLabel = shouldBreakNudge && !isActive;

  return (
    <>
      <div className="session-controls-sticky-row">
        <div className="session-controls-left-cluster" style={{ display: 'flex', gap: '3px', alignItems: 'center', flexShrink: 0 }}>
          <ElementHintTarget
            elementId="header-app-logo-btn"
            heading="App logo"
            body={`CatIntAssist build info. Version: ${APP_VERSION_LABEL}. Click: back to transcription.`}
            color="#a855f7"
          >
            <button
              id="header-app-logo-btn"
              type="button"
              className={`btn-icon tiny-btn app-logo-btn app-logo-btn--${appStatus.tone}${(soundboardOpen || goalsOpen) ? ' app-logo-btn--back-to-work' : ''}`}
              aria-label={`CatIntAssist ${appStatus.label}. ${APP_VERSION_LABEL}.${(soundboardOpen || goalsOpen) ? ' Alternate view open — click the cat to go back to work.' : ' Back to transcription.'}`}
              title={(soundboardOpen || goalsOpen) ? `${appStatus.title} ${APP_VERSION_LABEL} — click: back to work view` : `${appStatus.title} ${APP_VERSION_LABEL} — click: back to transcription`}
              onClick={handleLogoBack}
            >
              <img
                className={`app-logo-img app-logo-img--${appStatus.tone}`}
                src={`${process.env.PUBLIC_URL || ''}/favicon-96x96.png`}
                alt=""
                width={22}
                height={22}
              />
            </button>
          </ElementHintTarget>

          {/* v4.98.0: autopilot armed indicator */}
          <AutopilotChip />

          {!isActive ? (
              <ConnectInterpretButton
                onSingle={connectOnSingle}
                onDouble={connectOnDouble}
                flash={connectFlash}
                disabled={false}
                size="top"
                label={connectLabel}
                requireDoubleTapIndicator={requireDoubleTapIndicator}
                onArmDoubleTap={onArmDoubleTap}
                pendingDoubleTapTitle={pendingDoubleTapTitle}
                singleTitle={connectSingleTitle}
                doubleTitle={connectDoubleTitle}
                micTestMode={micTestMode}
                audioSourceMode={configuredAudioSourceMode}
                providerReady={!apiKeyMissing && !vaultNeedsDecrypt}
              />
          ) : (
            <ElementHintTarget
              elementId="header-stop-btn"
              guideKey="stop"
              heading="Stop call / keep tab"
              body="End this interpretation session. Deepgram stops, but the selected Tab stays ready for the next call."
              color="#94a3b8"
            >
              <button
                id="header-stop-btn"
                data-guide="stop"
                type="button"
                className="header-chrome-btn"
                onClick={handleStop}
                title="STOP CALL — TAB STAYS READY"
              >
                <StopIcon size={14} />
              </button>
            </ElementHintTarget>
          )}

          {/* Today timers: on-call vs off-call at a glance (v4.86.5) */}
          <ElementHintTarget
            elementId="header-oncall-timers"
            heading="On / off-call today"
            body="📞 minutes on calls vs 📡 minutes off-call (avail + break) today."
            color="#34d399"
          >
            <span
              id="header-oncall-timers"
              className="header-oncall-timers"
              title="Today: 📞 on-call vs 📡 off-call (avail + break)"
            >
              <span
                id="header-daily-income"
                style={{ color: '#fb923c', fontWeight: 800, fontSize: '0.72rem' }}
                title="Earned today in AR$ (live) — v4.87.0"
              >
                AR${Math.round(dailyIncomeArs).toLocaleString('en-US')}
              </span>
              <span id="header-oncall-mins" style={{ color: '#34d399' }}>📞{Math.round(onCallMins)}m</span>
              <span id="header-offcall-mins" style={{ color: '#fbbf24' }}>📡{Math.round(offCallMins)}m</span>
            </span>
          </ElementHintTarget>

          {/* Break lives off-call only — unmounted (not dimmed) in-call to
              free a left-cluster slot at 900px. */}
          {!isActive && (
          <ElementHintTarget
            elementId="header-break-btn"
            heading="Break"
            body="Coffee icon only during calls. Off-call, expands to BREAK after 90m without a break."
            color="#fb923c"
          >
            <button
              id="header-break-btn"
              className={`btn-emoji header-accent-break${showBreakLabel ? ' has-label' : ''}`}
              onClick={isBreakActive ? stopBreak : onStartBreak}
              style={{
                color: '#fff',
              }}
              title={shouldBreakNudge ? '90+ min on call — take a break when you can' : 'BREAK'}
            >
              {showBreakLabel ? (
                <span className="header-break-nudge">
                  <CoffeeIcon size={14} />
                  <span>BREAK</span>
                </span>
              ) : (
                <CoffeeIcon size={14} />
              )}
            </button>
          </ElementHintTarget>
          )}

          {!isActive && onOpenGoalsView && (
            <ElementHintTarget
              elementId="header-goal-btn"
              guideKey="goal-wheel"
              heading="Goal Tracking view"
              body="Goal dial + month calendar. Tap to open the Goal Tracking view."
              color="#94a3b8"
            >
              <button
                id="header-goal-btn"
                data-guide="goal-wheel"
                type="button"
                className={`header-chrome-btn${goalsOpen ? ' is-on' : ''}`}
                onClick={onOpenGoalsView}
                title="Goal Tracking — dial + month calendar (off-call)"
              >
                <TargetIcon size={14} />
              </button>
            </ElementHintTarget>
          )}

          {isActive && (
            <>
              <ElementHintTarget
                elementId="header-hold-btn"
                heading="Hold"
                body="Pause interpretation without ending the call. Shows elapsed hold time when active."
                color="#f59e0b"
              >
                <button
                  id="header-hold-btn"
                  type="button"
                  className={`header-chrome-btn header-accent-hold${isHold ? ' is-active' : ''}`}
                  onClick={() => setIsHold(!isHold)}
                  title={isHold ? `On hold — ${formatTime(holdSeconds)} elapsed. Tap to resume.` : 'Hold — pause without ending the call'}
                >
                  {isHold ? <HoldReadout isHold holdSeconds={holdSeconds} /> : <PauseIcon size={14} />}
                </button>
              </ElementHintTarget>
              <ElementHintTarget
                elementId="header-zap-btn"
                heading="Zap reconnect"
                body={disableZap ? 'ZAP disabled — stream is healthy.' : 'Force-reconnect the audio stream when STT goes stale.'}
                color="#94a3b8"
              >
                <button
                  id="header-zap-btn"
                  type="button"
                  className={`header-chrome-btn${isZapping ? ' is-on' : ''}${sttStall ? ' header-zap-stall' : ''}`}
                  onClick={disableZap ? undefined : () => onReconnectStream()}
                  disabled={disableZap}
                  title={
                    disableZap
                      ? 'ZAP disabled'
                      : sttStall
                        ? 'Transcription stopped — press ZAP to reconnect'
                        : 'ZAP - Reconnect Audio Stream'
                  }
                >
                  <ZapIcon size={14} />
                </button>
              </ElementHintTarget>
            </>
          )}

        </div>

        <div className="session-controls-center">
          {isActive ? (
            <div className="call-micro-bar-row">
            {callModeExpanded ? (
            <div className="call-micro-bar-center">
              <span
                className="call-micro-bar-slot call-micro-bar-hold"
                title="Non-doctor hold time (resets on English speech)"
                style={{ visibility: !showConnecting && silenceCount > 30 ? 'visible' : 'hidden' }}
              >
                {formatTime(Math.max(0, Math.floor((Date.now() - lastEnglishActivityTime) / 1000)))}
              </span>

              <MicroBarTimer showConnecting={showConnecting} connectionMessage={connectionMessage} sessionSeconds={sessionSeconds} />

              {!showConnecting && (
                <span className="call-micro-bar-slot call-micro-bar-earnings">
                  <SlotMicroValue text={`AR$${sessionArsLive}`} />
                </span>
              )}

              {showConnecting && (
                <span
                  className="call-micro-bar-slot call-micro-bar-disconnected"
                  title="No audio packets received — slacking off time"
                  style={{
                    fontFamily: 'var(--font-mono)',
                    color: '#fbbf24',
                    fontSize: '0.7rem',
                    fontWeight: 900,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    maxWidth: '10ch',
                  }}
                >
                  {slackText}
                </span>
              )}

              {!showConnecting && (
                <span
                  className="call-micro-bar-slot call-micro-bar-nudge"
                  title="Working 90+ minutes without a break"
                  style={{ visibility: minutesSinceLastBreak > 90 ? 'visible' : 'hidden' }}
                >
                  🍕 {Math.floor(minutesSinceLastBreak)}m
                </span>
              )}
              <span className="call-micro-bar-slot call-micro-bar-reserved">
                {/* v4.97.0: reserved slot carries the mic-verify chip in call */}
                <MicVerifyChip title="Client mic + last MIC VERIFY verdict — run TEST from the idle pane before going avail" />
              </span>
            </div>
            ) : (
            <div className="call-micro-bar-center call-micro-bar-center--compact" title={`ON CALL live — ${formatTime(sessionSeconds)}`}>
              <MicroBarTimer showConnecting={showConnecting} connectionMessage={connectionMessage} sessionSeconds={sessionSeconds} />
            </div>
            )}
              {/* v4.164.0: in meter-only mode .session-controls-center is
                  display:none (index.css:320) and the meter row renders its own
                  chip. Mounting both put the same #daily-targets-chip id in the
                  DOM twice, with two independent ResizeObserver fits. */}
              {!meterOnly && (
                <StickyTargetsChip dailyMinutes={dailyMinutes} monthlyMinutes={monthlyMinutes} workDays={workDays} breakMinutes={breakMinutes} ratePerMinute={ratePerMinute} goalMinutes={goalMinutes} goalSetAt={goalSetAt} goalBaseMinutes={goalBaseMinutes} onOpenGoalsView={onOpenGoalsView} />
              )}
            </div>
          ) : (
            <>
            <div className="off-call-inline-row" id="off-call-inline-row">
              {/* Status strip only mounts when there is something to say —
                  zombie/error/connecting. Status + gap counters share ONE row so
                  off-call center never stacks or pushes the TAB/VB strip down.
                  (v4.132.0: the mic chip left this row for Settings → Audio.) */}
              {(isZombieCall || connectionState === 'error' || connectionState === 'connecting') && (
              <div className="call-micro-bar-center off-call-status-bar" title={offCallStatusLabel}>
                <span
                  className="call-micro-bar-slot call-micro-bar-status"
                  style={{
                    gridColumn: '1 / -1',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    color: isZombieCall
                      ? '#fbbf24'
                      : connectionState === 'error'
                        ? '#f87171'
                        : '#fbbf24',
                  }}
                >
                  {offCallStatusLabel}
                </span>
              </div>
              )}
              {/* v4.124.0: OFF-call gap + LAST call counters — one line, ticks live
                  (gap since last call end; live avail before the first call). */}
              <div
                className="off-call-gap-row"
                id="off-call-gap-row"
                title={lastCallValidToday
                  ? `Off call for ${formatTime(offCallGapSecs)} — last call was ${formatTime(lastCallSeconds || 0)}`
                  : 'Off call right now (live) — LAST appears after your first call today'}
                aria-label={lastCallValidToday
                  ? `Off call for ${formatTime(offCallGapSecs)}, last call was ${formatTime(lastCallSeconds || 0)}`
                  : `Off call live ${formatTime(availSeconds || 0)}, no call banked yet today`}
              >
                <span className="status-bar-timer status-bar-timer--off-call">
                  <span className="status-bar-timer-icon" aria-hidden>🚪</span>
                  <span>OFF&nbsp;</span>
                  <SlotMicroValue text={formatTime(lastCallValidToday ? offCallGapSecs : (availSeconds || 0))} />
                </span>
                {lastCallValidToday && (
                  <>
                    <span className="status-bar-timer-sep" aria-hidden>·</span>
                    <span className="status-bar-timer status-bar-timer--on-call">
                      <span className="status-bar-timer-icon" aria-hidden>📞</span>
                      <span>LAST&nbsp;</span>
                      <SlotMicroValue text={formatTime(lastCallSeconds || 0)} />
                    </span>
                  </>
                )}
                {/* v4.124.0: DAY totals ride here too — #header-oncall/offcall-mins
                    are CSS-hidden at ≤1100px, so 900px would otherwise show none. */}
                <span className="status-bar-timer-sep" aria-hidden>·</span>
                <span className="status-bar-timer status-bar-timer--on-call" title="On-call today (total)">
                  <span>📞{Math.round(onCallMins || 0)}m</span>
                </span>
                <span className="status-bar-timer status-bar-timer--off-call" title="Off-call today (total)">
                  <span>📡{Math.round(offCallMins || 0)}m</span>
                </span>
              </div>
              {/* v4.132.0: the off-call MIC VERIFY chip moved out of this row (it
                  lives in Settings → Audio → Devices now). The ON-CALL chip in the
                  call micro bar stays — do not re-add a chip here: this row must
                  stay ONE non-stacking line (see comment above). */}
            </div>
            {/* v4.133.0: the 💵⏱☕ targets chip gets its OWN line under the gap
                counters — NOT the audio strip trailing slot. The chip measures
                `closest('.session-controls-center')` (DailyTargetsChip.js:119),
                so it must live inside the center column: the audio row has no
                such ancestor, so the chip measured the whole audio line, kept all
                three pairs and painted over the TAB/VB proof spans (3884ce5 bug). */}
            <div className="off-call-targets-row" id="off-call-targets-row">
              <StickyTargetsChip dailyMinutes={dailyMinutes} monthlyMinutes={monthlyMinutes} workDays={workDays} breakMinutes={breakMinutes} ratePerMinute={ratePerMinute} goalMinutes={goalMinutes} goalSetAt={goalSetAt} goalBaseMinutes={goalBaseMinutes} onOpenGoalsView={onOpenGoalsView} />
            </div>
            </>
          )}
        </div>

        <div className="session-controls-right-cluster">
          {(isActive || isZombieCall) && (
            <button
              id="header-meter-hud-btn"
              type="button"
              className="header-chrome-btn"
              onClick={onToggleMeterHud}
              title={meterOnly ? 'Meter-only HUD — click to exit' : 'Meter-only HUD (extra compact): just timeline meter + targets'}
              aria-pressed={meterOnly}
              style={{ fontSize: '0.7rem', opacity: meterOnly ? 1 : 0.65 }}
            >
              📊
            </button>
          )}
          {((isActive || isZombieCall) && !callModeExpanded) && (
            <ElementHintTarget
              elementId="header-expand-btn"
              heading="Expand header"
              body="Show full header controls during an active call."
              color="#94a3b8"
            >
              <button
                id="header-expand-btn"
                type="button"
                className="header-chrome-btn"
                onClick={() => setCallModeExpanded(true)}
                title="Expand Header"
              >
                <ChevronUpIcon size={14} />
              </button>
            </ElementHintTarget>
          )}
          {(isActive || isZombieCall) && callModeExpanded && (
            <ElementHintTarget
              elementId="header-compact-btn"
              heading="Compact header"
              body="Minimize header to maximize transcription space during calls."
              color="#94a3b8"
            >
              <button
                id="header-compact-btn"
                type="button"
                className="header-chrome-btn"
                onClick={() => setCallModeExpanded(false)}
                title="Compact Header"
              >
                <ChevronDownIcon size={14} />
              </button>
            </ElementHintTarget>
          )}

          {/* v4.165.0: the EN | ES button left the header. It was the widest text
              button in the right cluster and it duplicated Settings -> Language,
              which has the full pair pickers. The 450ms hold already opened
              Settings for it, so nothing is lost but the two seconds of width.
              NOTE: this also removed the one-click STT lane switch from the
              header. If that ever needs to be back, put it in Settings, not
              here. */}

          {/* v4.176.0: ▦ = 12-cell grid under the compact call header. Some
              calls it is a nuisance, some a lifesaver — the operator decides. */}
          {(isActive || isZombieCall) && onToggleGridInCall && (
            <button
              id="header-grid-toggle-btn"
              type="button"
              className="header-chrome-btn"
              onClick={onToggleGridInCall}
              aria-pressed={gridInCall}
              title={gridInCall ? 'Hide the 12-cell grid during calls' : 'Show the 12-cell grid during calls'}
              style={{ fontSize: '0.7rem', opacity: gridInCall ? 1 : 0.55 }}
            >
              ▦
            </button>
          )}
          {/* v4.176.0: IO = audio-route strip. Hidden or not, it returns by
              itself while audio is broken (shouldShowIoStrip). */}
          {onToggleIoStrip && (
            <button
              id="header-io-toggle-btn"
              type="button"
              className="header-chrome-btn"
              onClick={onToggleIoStrip}
              aria-pressed={ioStripOn}
              title={ioStripOn ? 'Hide the audio-route strip (returns by itself if audio breaks)' : 'Show the audio-route strip'}
              style={{ fontSize: '0.6rem', fontWeight: 700, opacity: ioStripOn ? 1 : 0.55 }}
            >
              IO
            </button>
          )}

          {showEndDayButton && (
            <ElementHintTarget
              elementId="header-end-day-btn"
              heading="End day"
              body="Close out the work day and reset daily tracking."
              color="#7c3aed"
            >
              <button
                id="header-end-day-btn"
                type="button"
                className="btn-emoji header-accent-logoff"
                onClick={onEndDay}
                title="END DAY"
              >
                <MoonIcon size={14} />
              </button>
            </ElementHintTarget>
          )}

          <SettingsButton />
        </div>
      </div>
      {/* Always visible: the Deepgram proof during a call (the TAB/VB switch and
          the "where do greetings go out" label moved to Settings in v4.166.0).
          v4.167.0: the Soundboard 🎛 and Greeting Editor ✎ props are no longer
          passed down. Those two buttons were this row's last two occupants and
          they were the only doors to those views; both now open from Settings →
          Studio. The cat logo below still closes the studio, because that is a
          "back to work" affordance, not an entrance. */}
      {shouldShowIoStrip({ ioStripOn, isActive, isZombieCall, audioAttached, virtualCableFailure }) && (
        <AudioRouteStatusBar
          micTestMode={micTestMode}
          setMicTestMode={setMicTestMode}
          tabStreamReady={tabStreamReady}
          cableStreamReady={cableStreamReady}
          configuredAudioSourceMode={configuredAudioSourceMode}
          attachedAudioSourceMode={attachedAudioSourceMode}
          virtualCableFailure={virtualCableFailure}
          audioAttached={audioAttached}
          connectionState={connectionState}
          connectProgress={connectProgress}
          lastDataTime={lastDataTime}
          isActive={isActive}
          isZombieCall={isZombieCall}
          totalOnCallSeconds={totalOnCallSeconds}
          totalOffCallSeconds={totalOffCallSeconds}
          dailyMinutes={dailyMinutes}
          monthlyMinutes={monthlyMinutes}
          breakMinutes={breakMinutes}
          ratePerMinute={ratePerMinute}
          onReconnectStream={onReconnectStream}
          onReconnectAudioSource={onReconnectAudioSource}
          onSwitchToTabShare={onSwitchToTabShare}
          onSwitchToVirtualCable={onSwitchToVirtualCable}
          onTestLocal={() => playTestToneLocal()}
          onTestRoute={async () => {
            const sinkId = localStorage.getItem('CATINTASSIST_SINK_ID');
            if (!sinkId) {
              document.getElementById('audio-route-sink-select')?.focus();
              return;
            }
            await playTestToneSink(sinkId);
          }}
          compact
          trailing={trailingSlot}
        />
      )}
    </>
  );
});

export const DashboardHeader = ({
  sttStall = null,
  onAttachAudio,
  onAttachAudioFresh,
  onStartCall,
  onStopAudio,
  onReconnectStream,
  sttLanguage,
  onToggleLanguage,
  onRecovery,
  connectionState,
  connectionMessage,
  connectProgress,
  lastDataTime,
  micTestMode = false,
  setMicTestMode,
  offCallWorkspace = null,
  onCycleWorkspace,
  showStudioHint = false,
  onConnectAnotherTab,
  tabStreamReady = false,
  cableStreamReady = false,
  configuredAudioSourceMode = "tab",
  attachedAudioSourceMode = "tab",
  virtualCableFailure = null,
  audioAttached = false,
  apiKeyRejected = false,
  settingsOpen = false,
  onOpenSoundboard,
  soundboardOpen = false,
  onOpenGoalsView,
  goalsOpen = false,
  onReconnectAudioSource,
  onSwitchToTabShare,
  onSwitchToVirtualCable,
}) => {
  const { isActive, sessionSeconds, sessionEarnings, stats, updateStat, stopSession, endDay, RATE_PER_MINUTE, arsRate, setArsRate, isBreakActive, breakSeconds, startBreak, stopBreak, availSeconds, isEditingScoreboard, setIsEditingScoreboard, visibleCards, toggleCard, visibleMetrics, toggleMetric, scoreboardPreset, applyScoreboardPreset, isNotesOpen, setIsNotesOpen, isToolbarVisible, setIsToolbarVisible, goalWorkDays, isZombieCall, isScoreboardHelpVisible, setIsScoreboardHelpVisible, isHold, setIsHold, holdSeconds, dailyTimeline, historyTimeline, dailyLog, lastActivityTime, lastEnglishActivityTime, isCallDetectionEnabled, setIsCallDetectionEnabled, callFocusMode, setCallFocusMode, minutesSinceLastBreak, vaultStatus, getMonthResyncPreview, reconcileMonthTotal, lastCallSeconds, lastCallEndedAt } = useSession();

  // v4.164.0: greeting-editor was missing here, so the Greeting Editor view got
  // the FULL scoreboard + progress stack rendered above it — and at <=900px the
  // header is capped at 88px with overflow:hidden, so that content was
  // unreachable rather than scrollable. All three workspace views hide the body.
  const headerMinimal = !isActive && (
    offCallWorkspace === 'soundboard'
    || offCallWorkspace === 'goals'
    || offCallWorkspace === 'greeting-editor'
  );
  const offCallScoreboardView = !isActive && offCallWorkspace === 'scoreboard';
  const studioView = offCallWorkspace === 'soundboard' ? 'soundboard' : 'scoreboard';

  useComponentVisibilityRefresh();
  const visCtx = { isActive, isZombieCall };
  const [languagePair, setLanguagePair] = useState(loadLanguagePair);
  useEffect(() => {
    const onPairChange = (e) => setLanguagePair(e.detail || loadLanguagePair());
    window.addEventListener(LANG_PAIR_CHANGED_EVENT, onPairChange);
    return () => window.removeEventListener(LANG_PAIR_CHANGED_EVENT, onPairChange);
  }, []);
  const languagePairLabel = formatPairShort(languagePair);
  const showProgressStack = shouldShowProgressStack(scoreboardPreset, visCtx);
  const showExpandedIncome = isComponentVisible('expanded_income_cards', visCtx) && !offCallScoreboardView;
  // v4.96.3: in-call expanded card always shows the numbers grid — it is the
  // only dense data face left there (game face hides in call), and hiding it
  // made the card a 242px empty void. Off-call visibility setting still rules.
  const showNumericGrid = isActive || isComponentVisible('scoreboard_numeric_grid', visCtx);

  const helpStyle = isScoreboardHelpVisible ? { outline: '1px dashed #ef4444', position: 'relative' } : {};
  const HelpLabel = ({ text }) => isScoreboardHelpVisible ? (
    <div style={{ position: 'absolute', top: '-8px', left: '4px', fontSize: '0.45rem', background: '#ef4444', color: 'white', padding: '0 3px', borderRadius: '2px', zIndex: 100, pointerEvents: 'none', fontWeight: 'bold', textTransform: 'uppercase', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>{text}</div>
  ) : null;
  const MetricPct = ({ children }) => (
    <div className="metric-cell-pct">{children}</div>
  );

  const showMetric = (key) => isEditingScoreboard || visibleMetrics[key];

  const MetricVisibilityToggle = ({ metricKey }) => isEditingScoreboard ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); toggleMetric(metricKey); }}
      style={{
        position: 'absolute', top: 2, right: 2, zIndex: 20,
        background: 'rgba(0,0,0,0.65)', border: '1px solid rgba(255,255,255,0.25)',
        borderRadius: 3, fontSize: '0.5rem', cursor: 'pointer', padding: '0 3px',
        opacity: visibleMetrics[metricKey] ? 1 : 0.45,
      }}
      title={visibleMetrics[metricKey] ? 'Hide cell' : 'Show cell'}
    >
      {visibleMetrics[metricKey] ? '👁' : '🚫'}
    </button>
  ) : null;

  const CardVisibilityToggle = ({ cardKey }) => isEditingScoreboard ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); toggleCard(cardKey); }}
      style={{
        position: 'absolute', top: 2, right: 2, zIndex: 20,
        background: 'rgba(0,0,0,0.65)', border: '1px solid rgba(255,255,255,0.25)',
        borderRadius: 3, fontSize: '0.5rem', cursor: 'pointer', padding: '0 3px',
        opacity: visibleCards[cardKey] ? 1 : 0.45,
      }}
      title={visibleCards[cardKey] ? 'Hide card' : 'Show card'}
    >
      {visibleCards[cardKey] ? '👁' : '🚫'}
    </button>
  ) : null;

  const toggleToolbar = useCallback(() => {
    const next = !isToolbarVisible;
    setIsToolbarVisible(next);
    if (next) {
      setIsNotesOpen(true);
      window.setTimeout(() => {
        window.dispatchEvent(new Event('catint_focus_notes'));
      }, 80);
    }
  }, [isToolbarVisible, setIsToolbarVisible, setIsNotesOpen]);

  const toggleQuickNotes = useCallback(() => {
    setIsNotesOpen((open) => {
      const next = !open;
      if (next) {
        window.setTimeout(() => {
          window.dispatchEvent(new Event('catint_focus_notes'));
        }, 80);
      }
      return next;
    });
  }, [setIsNotesOpen]);
  const { outputDevices, inputDevices, selectedSinkId, selectedMicId, changeSinkId, changeMicId, fetchDevices } = useAudioSettings();
  const audioEngine = useProgressiveAudio();
  const { playChaChing } = useRewardAudio();

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [celebration, setCelebration] = useState(null); // Keep celebration for sound logic
  // v4.113.0: goalWorkDays + goal dial moved to the Goal Tracking view;
  // the workdays basis now lives in SessionContext (goalWorkDays) so it stays
  // fresh when the view saves while this header stays mounted.
  const [displayBounty, setDisplayBounty] = useState(0);
  const [isBountyAnimating, setIsBountyAnimating] = useState(false);
  const [timeEditMode, setTimeEditMode] = useState(null); // 'call' | 'break' | null
  const [scoreView, setScoreView] = useState(() => getPresetConfig(scoreboardPreset).scoreView || 'game'); // 'game' | 'numbers'
  const [silenceCount, setSilenceCount] = useState(0);
  const [rhythmOpen, setRhythmOpen] = useState(false); // v4.101.0: rhythm/schedule panel

  useEffect(() => {
    const cfg = getPresetConfig(scoreboardPreset);
    if (cfg?.scoreView) setScoreView(cfg.scoreView);
  }, [scoreboardPreset]);

  useEffect(() => {
    if (isActive && scoreboardPreset === 'minimal') setIsCollapsed(true);
  }, [isActive, scoreboardPreset]);
  const [hoveredTimelineEvent, setHoveredTimelineEvent] = useState(null);
  const { show: showElementHint, hide: hideElementHint } = useElementHint();
  const [isZapping, setIsZapping] = useState(false);
  const handleZap = useCallback(async () => {
    setIsZapping(true);
    try {
      await onReconnectStream?.();
    } finally {
      setTimeout(() => setIsZapping(false), 1200);
    }
  }, [onReconnectStream]);

  // ANCHORED-GOAL follow-up: the weekly pill shows the COMMITMENT. Deriving it
  // by dividing the month total (which mid-month is prorated: worked so far +
  // commitment × workdays left) showed "13h/wk goal" for a 35h/Wk commitment,
  // and hardcoded the 22d/5d basis instead of the real workdays basis.
  const goalBasis = savedGoalWorkDays(stats, goalWorkDays) || 22;
  const committedPerWorkday = perWorkdayFromStats(stats, { workDays: goalBasis });
  const weeklyGoalHours = Math.round(
    weeklyHoursFromCommitment(committedPerWorkday, daysPerWeekOf(goalBasis)),
  );

  const [showAsHours, setShowAsHours] = useState(false);
  const [rateView, setRateView] = useState('effective'); // 'effective' | 'active'
  const [overtimeMode, setOvertimeMode] = useState('tail'); // 'tail' | 'under'
  const [gridInCall, setGridInCall] = useState(() => loadHeaderToggle('gridInCall'));
  const [ioStripOn, setIoStripOn] = useState(() => loadHeaderToggle('ioStrip'));
  const toggleGridInCall = useCallback(() => setGridInCall((v) => saveHeaderToggle('gridInCall', !v)), []);
  const toggleIoStrip = useCallback(() => setIoStripOn((v) => saveHeaderToggle('ioStrip', !v)), []);
  const [callModeExpanded, setCallModeExpanded] = useState(false); // User can pin full header during calls
  // v4.88.5: 3rd HUD mode during calls — meter-only (goal meter as extra-compact HUD)
  const [hudMeterOnly, setHudMeterOnly] = useState(() => localStorage.getItem('catint_hud_meter_only_v1') === '1');
  // v4.164.0 — which pill was just copied, so it can flash a ✓. One id at a
  // time: copying a second pill replaces the first, which is what you want.
  const [copiedPill, setCopiedPill] = useState(null);
  const copyPill = useCallback((id, value) => {
    const text = String(value ?? '').trim();
    if (!text) return;
    const done = () => setCopiedPill(id);
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done, () => {});
        return;
      }
    } catch (_) { /* fall through */ }
    // No clipboard (insecure context / denied): the number is on screen anyway,
    // so do not pretend the copy worked.
  }, []);
  const [offCallMetricsExpanded, setOffCallMetricsExpanded] = useState(() => {
    try {
      return localStorage.getItem(OFF_CALL_METRICS_EXPANDED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // User-resizable scoreboard height (vh), persisted (v4.86.5).
  const [scoreboardMaxVh, setScoreboardMaxVh] = useState(() => {
    try {
      const v = Number(localStorage.getItem(SCOREBOARD_MAX_VH_KEY));
      return v >= 18 && v <= 80 ? v : 38;
    } catch (_) { return 38; }
  });
  const startScoreboardResize = useCallback((e) => {
    e.preventDefault();
    const headerEl = e.currentTarget.parentElement;
    const onMove = (ev) => {
      const rect = headerEl.getBoundingClientRect();
      const vh = Math.round(Math.min(80, Math.max(18, ((ev.clientY - rect.top) / window.innerHeight) * 100)));
      setScoreboardMaxVh(vh);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setScoreboardMaxVh((v) => {
        try { localStorage.setItem(SCOREBOARD_MAX_VH_KEY, String(v)); } catch (_) {}
        return v;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);

  const toggleOffCallMetricsExpanded = useCallback(() => {
    setOffCallMetricsExpanded((prev) => {
      const next = !prev;
      // The arrow means “show the comprehensive scoreboard”, not the compact
      // game card. Open directly on the 12 metric grid.
      if (next) setScoreView('numbers');
      try {
        localStorage.setItem(OFF_CALL_METRICS_EXPANDED_KEY, next ? 'true' : 'false');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const headerCallCompact = (isActive || isZombieCall) && !callModeExpanded;
  // v4.88.5: meter-only HUD — 3rd mode during calls (compact / expanded / meter)
  const meterOnlyMode = headerCallCompact && hudMeterOnly;
  const toggleMeterHud = useCallback(() => {
    setHudMeterOnly((v) => {
      if (!v) setCallModeExpanded(false); // entering meter mode always starts from compact
      return !v;
    });
  }, []);

  const expandOffCallMetrics = useCallback(() => {
    setOffCallMetricsExpanded(true);
    setScoreView('numbers');
    try {
      localStorage.setItem(OFF_CALL_METRICS_EXPANDED_KEY, 'true');
    } catch {
      /* ignore */
    }
  }, []);

  const handleQuickScoreView = useCallback((view) => {
    setScoreView(view);
    expandOffCallMetrics();
  }, [expandOffCallMetrics]);

  useEffect(() => {
    const iv = setInterval(() => {
      setSilenceCount(Math.floor((Date.now() - lastActivityTime) / 1000));
    }, 1000);
    return () => clearInterval(iv);
  }, [lastActivityTime]);

  // Auto-reset expanded header when call ends
  useEffect(() => {
    if (!isActive) setCallModeExpanded(false);
  }, [isActive]);

  // v4.88.5: persist meter-only HUD choice; never expands during a call end state
  useEffect(() => {
    localStorage.setItem('catint_hud_meter_only_v1', hudMeterOnly ? '1' : '0');
  }, [hudMeterOnly]);

  useEffect(() => {
    const onPrepare = (e) => {
      const d = e.detail || {};
      if (d.expandMetrics === true) {
        setOffCallMetricsExpanded(true);
        try {
          localStorage.setItem(OFF_CALL_METRICS_EXPANDED_KEY, 'true');
        } catch {
          /* ignore */
        }
      }
      if (d.expandMetrics === false) {
        setOffCallMetricsExpanded(false);
        try {
          localStorage.setItem(OFF_CALL_METRICS_EXPANDED_KEY, 'false');
        } catch {
          /* ignore */
        }
      }
      if (d.scoreView === 'game' || d.scoreView === 'numbers') setScoreView(d.scoreView);
    };
    window.addEventListener('cat_guide_prepare_view', onPrepare);
    return () => window.removeEventListener('cat_guide_prepare_view', onPrepare);
  }, []);

  // Break button: icon-only until 90m on call without a break (matches mic-bar nudge).
  const breakNudgeStage = isActive && !isBreakActive
    ? (minutesSinceLastBreak >= 110 ? 2 : minutesSinceLastBreak >= 90 ? 1 : 0)
    : 0;
  const shouldBreakNudge = breakNudgeStage > 0;
  const lastBreakNudgeStageRef = useRef(0);
  useEffect(() => {
    if (!isActive || isBreakActive) {
      lastBreakNudgeStageRef.current = 0;
      return;
    }
    if (breakNudgeStage > 0 && lastBreakNudgeStageRef.current !== breakNudgeStage) {
      lastBreakNudgeStageRef.current = breakNudgeStage;
      audioEngine.playWarningPing?.();
    }
  }, [breakNudgeStage, isActive, isBreakActive, audioEngine]);

  const startOfToday = new Date().setHours(0,0,0,0);
  const timelineStart = startOfToday + 9 * 3600000;
  const timelineEnd = startOfToday + 23 * 3600000;
  const timelineDuration = timelineEnd - timelineStart;

  const getTimelinePos = (time) => {
    if (!time) return getTimelinePos(Date.now());
    const t = typeof time === 'number' ? time : new Date(time).getTime();
    return Math.max(0, Math.min(100, ((t - timelineStart) / timelineDuration) * 100));
  };

  // Daily shift bar: 9am-6pm (9h). Overtime tracked separately.
  const DAILY_SHIFT_START = 9;
  const DAILY_SHIFT_END = 18;
  const dailyShiftStartMs = startOfToday + DAILY_SHIFT_START * 3600000;
  const dailyShiftEndMs = startOfToday + DAILY_SHIFT_END * 3600000;
  const dailyShiftDuration = dailyShiftEndMs - dailyShiftStartMs;

  const getDailyTimelinePos = (time) => {
    if (!time) return getDailyTimelinePos(Date.now());
    const t = typeof time === 'number' ? time : new Date(time).getTime();
    return ((t - dailyShiftStartMs) / dailyShiftDuration) * 100; // allow >100 and <0
  };

  const getOvertimePos = (time) => {
    const t = typeof time === 'number' ? time : new Date(time).getTime();
    const maxOvertimeMs = 6 * 3600000; // 18:00-00:00
    return Math.max(0, Math.min(100, ((t - dailyShiftEndMs) / maxOvertimeMs) * 100));
  };

  // Mini-timeline renderer for day notches
  const MiniDayTimeline = ({ dateStr, currentTimeline, dailyMins, goalMins }) => {
    const timeline = currentTimeline || historyTimeline[dateStr];
    
    // Fallback: If no chronological data, just show a solid block based on minutes worked
    if (!timeline || timeline.length === 0) {
      const fillPct = Math.min(100, (dailyMins / (goalMins || 1)) * 100);
      return (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.05)' }}>
           <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${fillPct}%`, background: dailyMins >= goalMins ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)' }} />
        </div>
      );
    }

    // Chronological Render (Orange/Blue thing)
    return (
      <div 
        title={`Waiting/Available: ${dailyMins}m worked today.`}
        style={{ position: 'absolute', inset: 0, background: 'rgba(251, 146, 60, 0.15)', cursor: 'crosshair' }}>
        {timeline.map((evt, i) => {
          const s = getTimelinePos(evt.start);
          const e = getTimelinePos(evt.end || Date.now());
          const startTime = new Date(evt.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
          const endTime = evt.end ? new Date(evt.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : 'Ongoing';
          const duration = Math.round(((evt.end || Date.now()) - evt.start) / 60000);

          if (evt.type === 'avail') return null; // Avail is the background orange
          
          return (
            <div key={i} 
              title={`${evt.type.toUpperCase()}: ${startTime} - ${endTime} (${duration}m)`}
              style={{ 
                position: 'absolute', left: `${s}%`, width: `${Math.max(0.5, e - s)}%`, 
                top: 0, bottom: 0, 
                background: evt.type === 'work' ? '#f87171' : (evt.type === 'break' ? '#fb923c' : 'transparent'),
                opacity: 0.8,
                zIndex: 10,
                borderLeft: '1px solid rgba(255,255,255,0.2)'
              }} />
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    if (isActive && sessionSeconds > 0 && sessionSeconds % 60 === 0) {
      // PRO LADDER / SOUNDSCAPE INTERPRETER: 
      // Every minute played is a coin earned. Richer sound as minutes pass.
      const currentMin = Math.floor(sessionSeconds / 60);
      playChaChing(currentMin);
      audioEngine.playTick(currentMin); // KEEP legacy bronze tick for depth
    }
  }, [isActive, sessionSeconds, playChaChing, audioEngine]);

  // handleStart and local starting logic REMOVED to favor unified App-level handlers passed via props

  const handleStop = useCallback(() => {
    setCallModeExpanded(false);
    stopSession((mins) => {
      // DENOMINATION PAYOUT LOGIC
      // Diamonds = 20m, Bills = 5m, Coins = 1m
      let rem = Math.round(mins);
      const diamonds = Math.floor(rem / 20); rem %= 20;
      const bills = Math.floor(rem / 5); rem %= 5;
      const coins = rem;

      // Denomination payout sound effects from audioEngine
      for(let i=0; i < diamonds; i++) setTimeout(() => audioEngine.playDiamond(), i * 400);
      for(let i=0; i < bills; i++) setTimeout(() => audioEngine.playBill(), (diamonds * 400) + (i * 300));
      for(let i=0; i < coins; i++) setTimeout(() => audioEngine.playCoin(), (diamonds * 400) + (bills * 300) + (i * 200));

      setCelebration({ type: 'call', label: `+AR$${Math.round(mins * RATE_PER_MINUTE * arsRate).toLocaleString('es-AR')}`, coins: Math.min(60, Math.floor(mins * 1.5)) });
      setTimeout(() => setCelebration(null), 4000);
    });
    // ponytail: keep tab/STT attached between calls — next green press starts call only.
    // Stop Deepgram for privacy, but preserve the active tab MediaStream.
    // The next Connect reuses it and does not open the tab picker.
    onStopAudio?.();
  }, [stopSession, RATE_PER_MINUTE, arsRate, audioEngine, onStopAudio]);

  const handleEndDay = () => {
    setCallModeExpanded(false);
    endDay((mins) => {
      audioEngine.playMetalChest();
      const dynamicItems = Math.min(200, Math.max(20, Math.floor(mins * 0.4)));
      setCelebration({ type: 'day', label: `Day Banked! +AR$${Math.round(mins * RATE_PER_MINUTE * arsRate).toLocaleString('es-AR')}`, coins: dynamicItems });
      setTimeout(() => setCelebration(null), 5000 + Math.min(3000, dynamicItems * 25));
    });
  };

  const handleStartBreak = useCallback(() => {
    startBreak();
  }, [startBreak]);

  const getWorkingDays = (y, m) => {
    let count = 0;
    const daysInMo = new Date(y, m + 1, 0).getDate();
    for (let d = 1; d <= daysInMo; d++) {
      const dow = new Date(y, m, d).getDay();
      if (dow !== 0 && dow !== 6) count++; // Mon-Fri
    }
    return count || 22;
  };

  const getRemainingWorkDays = (y, m, dStart) => {
    let count = 0;
    const dInMo = new Date(y, m + 1, 0).getDate();
    for (let d = dStart; d <= dInMo; d++) {
      const dow = new Date(y, m, d).getDay();
      if (dow !== 0 && dow !== 6) count++; 
    }
    return count;
  };

  // Calculations
  const now = new Date();
  const year = now.getFullYear(), month = now.getMonth(), currentDay = now.getDate();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const remainingDays = daysInMonth - currentDay + 1;
  const minutesBeforeToday = Math.max(0, stats.monthlyMinutes - stats.dailyMinutes);
  const remainingMinutesFromStartOfDay = Math.max(0, stats.goalMinutes - minutesBeforeToday);

  const remainingWorkDays = getRemainingWorkDays(year, month, currentDay);
  // ANCHORED-GOAL follow-up: with a stored commitment the day floor IS the
  // commitment (a banked mid-month month total ÷ business days would floor
  // today's goal at 152m instead of the promised 420m). Legacy stats keep the
  // exact old formula.
  const baseYield = Number(stats.goalPerWorkdayMinutes) > 0
    ? committedPerWorkday
    : (stats.goalMinutes || 5500) / (getWorkingDays(year, month) || 22);
  
  // REALISTIC CATCH-UP: Divide remaining by workdays, but cap at 600m (10h)
  const rawCatchUp = remainingWorkDays > 0 ? (remainingMinutesFromStartOfDay / remainingWorkDays) : baseYield;
  const dailyGoal = Math.min(600, Math.max(baseYield, rawCatchUp));
  
  const unbankedMins = isActive ? (sessionSeconds / 60) : 0;
  const totalDailyMins = stats.dailyMinutes + unbankedMins;
  const liveBreakMins = (stats.dailyBreakMinutes || 0) + (breakSeconds / 60);
  const rawTotalOffCallMins = (stats.dailyAvailMinutes || 0) + (stats.dailyBreakMinutes || 0) + (availSeconds / 60) + (breakSeconds / 60);
  // v4.101.2 sanity clamp: off-call can never exceed wall-clock since 09:00
  // minus on-call — guards against any leftover-crossing-midnight inflation
  // (the "632m off-call on a <8h shift" scare). Root fix is the live-rollover
  // seal in SessionContext; this clamps anything already banked today.
  const wallClockSince9Mins = (() => {
    const nowD = new Date();
    const nine = new Date(nowD.getFullYear(), nowD.getMonth(), nowD.getDate(), 9, 0, 0);
    return Math.max(0, (nowD - nine) / 60000);
  })();
  const offCallCapMins = Math.max(0, wallClockSince9Mins - totalDailyMins);
  const totalOffCallMins = Math.min(rawTotalOffCallMins, offCallCapMins);
  const totalOffCallSeconds = Math.max(0, Math.min(Math.floor(
    ((stats.dailyAvailMinutes || 0) + (stats.dailyBreakMinutes || 0)) * 60
    + (availSeconds || 0)
    + (breakSeconds || 0)
  ), Math.floor(offCallCapMins * 60)));
  const totalOnCallSeconds = Math.max(0, Math.floor((stats.dailyMinutes || 0) * 60) + (isActive ? Math.floor(sessionSeconds || 0) : 0));
  const compactWorkdaySegments = dailyTimeline
    .map((evt, index) => {
      const start = Math.max(dailyShiftStartMs, evt.start || dailyShiftStartMs);
      const end = Math.min(dailyShiftEndMs, evt.end || now.getTime());
      const left = Math.max(0, Math.min(100, ((start - dailyShiftStartMs) / dailyShiftDuration) * 100));
      const right = Math.max(0, Math.min(100, ((end - dailyShiftStartMs) / dailyShiftDuration) * 100));
      return { index, type: evt.type, left, width: Math.max(0, right - left) };
    })
    .filter((segment) => segment.width > 0);
  const compactShiftRemainingSeconds = Math.max(0, Math.floor((dailyShiftEndMs - now.getTime()) / 1000));

  // CATCH-UP LOGIC: Dynamic shifts and SUCCESS ZONES
  const ABSOLUTE_END = 23;
  const currentTime = now.getHours() + (now.getMinutes() / 60);
  const shiftElapsedRatio = Math.min(1, Math.max(0, (currentTime - DAILY_SHIFT_START) / (DAILY_SHIFT_END - DAILY_SHIFT_START)));

  // Log-off nudge (progressive fill + shine near 18:00, off-call only)
  const logoffGlowWindowStart = 17;
  const logoffGlowWindowEnd = 18;
  const logoffGlowProgress =
    !isActive && !isBreakActive
      ? Math.min(1, Math.max(0, (currentTime - logoffGlowWindowStart) / (logoffGlowWindowEnd - logoffGlowWindowStart)))
      : 0;
  const shouldLogoffShine = !isActive && !isBreakActive && currentTime >= 17.75;
  
  const getDayEmoji = () => {
    if (currentTime < 13) return '🌅';
    if (currentTime < 17) return '☀️';
    return '🌙';
  };
  const activeDayEmoji = getDayEmoji();

  // Condensed View metrics
  const liveDailyArs = Math.round(totalDailyMins * RATE_PER_MINUTE * arsRate);
  const dailyTargetArs = Math.round(dailyGoal * RATE_PER_MINUTE * arsRate);
  // v4.99.2: month money includes the live call's unbanked minutes — same
  // convention as MINS TODAY, so the grid never contradicts itself mid-call.
  const monthlyArs = Math.round((stats.monthlyMinutes + unbankedMins) * RATE_PER_MINUTE * arsRate);
  const monthlyTargetArs = Math.round(stats.goalMinutes * RATE_PER_MINUTE * arsRate);
  const currentBounty = Math.max(0, dailyTargetArs - liveDailyArs);
  const sessionArsLive = Math.round(sessionEarnings * arsRate);
  // liveDailyArs already includes the current call (totalDailyMins = dailyMinutes + unbankedMins),
  // so this is the live "$ today" figure as-is — don't add sessionArsLive again or it double-counts.
  const todayArsLive = liveDailyArs;

  // v4.95.4: perf live counters — old odometer (LiveRollingNumber, 30-div
  // strip per digit + per-digit timers) replaced by 1Hz StatNumber ticks.
  // Values already re-render every second via sessionSeconds/availSeconds,
  // and AR$ TODAY moves ~2 AR$/s, so the roll is visible with zero extra
  // timers, nodes, or layout thrash. tabular-nums keeps width stable.
  const renderLiveArs = (discreteValue, size = 'lg', prefix = 'AR$') => (
    <StatNumber value={discreteValue} prefix={prefix} size={size} />
  );

  const renderSessionArs = (size = 'sm', prefix = 'AR$') => renderLiveArs(sessionArsLive, size, prefix);

  const metricTooltipData = React.useMemo(() => ({
    1: {
      icon: '🕒',
      heading: 'MINS TODAY',
      color: '#f87171',
      body: 'Total minutes you have “banked” today so far (includes current call time). When this climbs toward your Daily Goal, your day quality and pacing stabilize.'
    },
    2: {
      icon: '🧭',
      heading: 'LEFT TODAY',
      color: '#fca5a5',
      body: 'How many minutes you still need to reach today’s goal. If this stays high late in the shift, switch pace: more productive calls, shorter idle gaps.'
    },
    3: {
      icon: '🎯',
      heading: 'TODAY GOAL',
      color: '#34d399',
      body: 'Your dynamic target for today’s minutes. It adapts to your shift timing and remaining workdays so you don’t grind blindly.'
    },
    4: {
      icon: '💵',
      heading: 'AR$ TODAY',
      color: '#34d399',
      body: 'Estimated earnings you’ve made today in AR$ (live if you’re on a call). Use it to judge whether your pacing is paying off right now.'
    },
    5: {
      icon: '🧾',
      heading: 'AR$ LEFT TODAY',
      color: '#fcd34d',
      body: 'Remaining AR$ needed to hit your daily money target. When this trends toward zero, you’re “done” even if your call still has time left.'
    },
    6: {
      icon: '⚡',
      heading: 'STAMINA RATIO',
      color: '#c084fc',
      body: 'On-call minutes divided by break (and downtime). Target is ~5.3x: it means you’re earning while keeping downtime under control.'
    },
    7: {
      icon: '🗓️',
      heading: 'AR$ MONTH',
      color: '#a855f7',
      body: 'Your banked monthly earnings vs the big monthly target. This is the “long game” number—small daily wins compound.'
    },
    8: {
      icon: '🧱',
      heading: 'AR$ LEFT MONTH',
      color: '#fcd34d',
      body: 'How much AR$ you still need to finish the month on target. Use it like a countdown: late-month rush is avoidable with small catch-up beats.'
    },
    9: {
      icon: '🚪',
      heading: 'OFF CALL',
      color: '#fdba74',
      body: 'Minutes you spent off-call today (avail + breaks). If this grows while you’re not actively earning, stamina ratio drops quickly.'
    },
    10: {
      icon: '📊',
      heading: 'MO AVG',
      color: '#a855f7',
      body: 'Average minutes per day so far this month. If the average is climbing, you’re likely ahead of your monthly pacing plan.'
    },
    11: {
      icon: '🔇',
      heading: 'SILENCE',
      color: '#ef4444',
      body: 'Time since the last audio activity (speech resets it). In-call, higher silence usually means the patient/user has gone quiet—pull them back gently.'
    },
    12: {
      icon: '📞',
      heading: 'CURR CALL',
      color: '#10b981',
      body: 'Current call duration and its live unbanked cash. It helps you separate “this call is productive” from “today is good overall.”'
    }
  }), []);

  const showMetricTooltip = useCallback((e, metricKey) => {
    const d = metricTooltipData[metricKey];
    if (!d) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const placement = rect.top < 160 ? 'below' : 'above';
    const y = placement === 'above' ? rect.top : rect.bottom;
    const x = rect.left + rect.width / 2;
    showElementHint(buildHintPayload({
      elementId: `metric-m${metricKey}`,
      heading: d.heading,
      body: d.body,
      icon: d.icon,
      color: d.color,
      x,
      y,
      placement,
    }));
  }, [metricTooltipData, showElementHint]);

  const hideMetricTooltip = useCallback(() => {
    hideElementHint();
  }, [hideElementHint]);

  // Progress-bar hover tooltip (ElementHint — same renderer as metric cards)
  const showProgressBarTooltip = useCallback((e, { icon, heading, color, body, elementId, guideKey }) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const placement = rect.top < 160 ? 'below' : 'above';
    const x = rect.left + rect.width / 2;
    const y = placement === 'above' ? rect.top : rect.bottom;

    showElementHint(buildHintPayload({
      elementId,
      guideKey,
      heading,
      body,
      icon,
      color,
      x,
      y,
      placement,
    }));
  }, [showElementHint]);

  useEffect(() => {
    if (Math.abs(displayBounty - currentBounty) > 1) {
      setIsBountyAnimating(true);
      const timer = setTimeout(() => {
        setDisplayBounty(currentBounty);
        setIsBountyAnimating(false);
      }, 500); 
      return () => clearTimeout(timer);
    }
  }, [currentBounty, displayBounty]);

  // Initialize bounty on load
  useEffect(() => {
    setDisplayBounty(currentBounty);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  let hoursLeftToAbsolute = Math.max(0, ABSOLUTE_END - currentTime);
  
  // Estimated workable mins from now
  const workableMinsRemaining = hoursLeftToAbsolute * 35;
  
  // realistic max is now computed inline in the daily bar label
  
  const remainingWorkdaysThisMonth = Math.max(0, remainingDays - 1);
  const monthlyMaxMins = stats.monthlyMinutes + workableMinsRemaining + (remainingWorkdaysThisMonth * 14 * 35);
  const monthlyRemainingCashVal = Math.round((workableMinsRemaining + remainingWorkdaysThisMonth * 14 * 35) * RATE_PER_MINUTE * arsRate);
  const monthlyMaxArs = Math.round(monthlyMaxMins * RATE_PER_MINUTE * arsRate).toLocaleString('es-AR');
  const actualDailyAverage = currentDay > 0 ? (stats.monthlyMinutes / currentDay) : 0;
  
  const monthElapsedRatio = currentDay / daysInMonth;
  const isMonthlyGoalMet = stats.monthlyMinutes >= stats.goalMinutes;
  const monthlyProgressRatio = stats.goalMinutes > 0 ? Math.min(1, stats.monthlyMinutes / stats.goalMinutes) : 0;
  const monthlyPendingRatio = stats.goalMinutes > 0 ? Math.min(1, (stats.monthlyMinutes + unbankedMins) / stats.goalMinutes) : 0;

  const getCompensatedLogOff = () => {
    if (!stats.dayStartTime) return '18:00';
    const start = new Date(stats.dayStartTime);
    const nineAM = new Date(stats.dayStartTime);
    nineAM.setHours(9, 0, 0, 0);
    const lateStartMs = Math.max(0, start.getTime() - nineAM.getTime());
    const totalBreakMs = (stats.dailyBreakMinutes || 0) * 60000;
    const safeDateString = stats.lastDate || new Date().toDateString();
    const end = new Date(safeDateString + ' 18:00:00');
    const compensatedTime = new Date(end.getTime() + lateStartMs + totalBreakMs);
    if (isNaN(compensatedTime.getTime())) return '18:00';
    return compensatedTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const shiftElapsedMins = stats.dayStartTime ? Math.max(0, (Date.now() - stats.dayStartTime) / 60000) : 0;
  const breakLimit = 90;
  const breakUsed = stats.dailyBreakMinutes || 0;
  const breakLeft = Math.max(0, breakLimit - breakUsed);
  const cashToTodayGoal = Math.max(0, dailyTargetArs - liveDailyArs);

  const staminaRatioVal = totalDailyMins / Math.max(0.1, liveBreakMins);
  const metricPcts = {
    minsToday: dailyGoal > 0 ? `${((totalDailyMins / dailyGoal) * 100).toFixed(0)}% of goal` : '—',
    leftToday: dailyGoal > 0 ? `${((Math.max(0, dailyGoal - totalDailyMins) / dailyGoal) * 100).toFixed(0)}% left` : '—',
    todayGoal: '100% target',
    arsToday: dailyTargetArs > 0 ? `${((todayArsLive / dailyTargetArs) * 100).toFixed(0)}% of tgt` : '—',
    arsLeft: dailyTargetArs > 0 ? `${((cashToTodayGoal / dailyTargetArs) * 100).toFixed(0)}% left` : '—',
    stamina: `${((staminaRatioVal / 5.3) * 100).toFixed(0)}% of 5.3x`,
    arsMonth: monthlyTargetArs > 0 ? `${((monthlyArs / monthlyTargetArs) * 100).toFixed(0)}% of tgt` : '—',
    arsLeftMonth: monthlyTargetArs > 0 ? `${((Math.max(0, monthlyTargetArs - monthlyArs) / monthlyTargetArs) * 100).toFixed(0)}% left` : '—',
    offCall: (totalDailyMins + totalOffCallMins) > 0 ? `${((totalOffCallMins / (totalDailyMins + totalOffCallMins)) * 100).toFixed(0)}% off-call` : '—',
    moAvg: baseYield > 0 ? `${((actualDailyAverage / baseYield) * 100).toFixed(0)}% vs pace` : '—',
    silence: silenceCount > 30 ? `${Math.min(100, ((silenceCount / 600) * 100)).toFixed(0)}% of 10m` : '—',
    currCall: isActive && dailyGoal > 0 ? `${(((sessionSeconds / 60) / dailyGoal) * 100).toFixed(0)}% of day` : '—',
  };

  const formatHoursMins = (totalMins) => {
    const h = Math.floor(totalMins / 60);
    const m = Math.floor(totalMins % 60);
    return `${h}h${m > 0 ? `${m}m` : ''}`;
  };

  const formatValue = (mins) => {
    if (showAsHours) return formatHoursMins(mins);
    return `${Math.round(mins)}m`;
  };

  // v4.95.4: per-second live time — same value as formatValue but with a
  // ticking seconds suffix so time visibly rolls every second (old dynamic
  // counter feel). Zero extra timers: parent already re-renders at 1Hz via
  // session/avail/break seconds. Off-call (avail) ticks off-call; on-call
  // cells tick only when isActive, else fall back to formatValue (frozen = correct).
  const formatLiveMins = (mins, shouldTick) => {
    if (!shouldTick) return formatValue(mins);
    if (showAsHours) {
      const totalSecs = Math.max(0, Math.floor(mins * 60));
      const h = Math.floor(totalSecs / 3600);
      const m = Math.floor((totalSecs % 3600) / 60);
      const s = totalSecs % 60;
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    const totalSecs = Math.max(0, Math.floor(mins * 60));
    const m = Math.floor(totalSecs / 60);
    const s = totalSecs % 60;
    return `${m}m ${String(s).padStart(2, '0')}s`;
  };

  // SIMPLIFIED 12-STEP ENGINE (5500m floor based)
  const WEEK_STEP = 1375; // 5500 / 4
  
  const milestones = React.useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => (i + 1) * WEEK_STEP);
  }, []);

  const milestoneLabels = [
    "Step 1: Floor Prep", "Step 2: Floor Rise", "Step 3: Floor Push", "🪜 LADDER: FLOOR (5500m)",
    "Step 5: Growth Prep", "Step 6: Growth Rise", "Step 7: Growth Push", "🚀 LADDER: GROWTH (11k)",
    "Step 9: Legend Prep", "Step 10: Legend Rise", "Step 11: Legend Push", "👑 LADDER: LEGEND (16.5k)"
  ];

  const nextMilestone = milestones.find(m => m > stats.monthlyMinutes) || milestones[milestones.length - 1];
  const currentIdx = milestones.indexOf(nextMilestone);
  const nextGoalLabel = milestoneLabels[currentIdx];

  const HARD_CUTOFF_HOUR = 24; 
  const currentHour = new Date().getHours();
  const currentMin = new Date().getMinutes();
  const minsToHardCutoff = Math.max(0, ((HARD_CUTOFF_HOUR * 60) - (currentHour * 60 + currentMin)));

  // ── MILESTONE TARGETS ──────────────────────────────────────────────────────
  // Milestone 1: 5500m/month (5 days/week)
  // Milestone 2: 480m/day (6 days/week)
  const workingDaysMo = getWorkingDays(year, month);
  const totalWindowMins = shiftElapsedMins + minsToHardCutoff;
  const timeRatio = totalWindowMins > 0 ? shiftElapsedMins / totalWindowMins : 0;

  const milestoneTargets = {
    m5500: 5500 / workingDaysMo,
    m480: 480,
    m5500Ideal: (5500 / workingDaysMo) * timeRatio,
    m480Ideal: 480 * timeRatio
  };
  
  const cutoffWarning = (() => {
    if (minsToHardCutoff <= 1)   return { label: 'MIDNIGHT DEADLINE', color: '#ef4444', pulse: true };
    if (minsToHardCutoff <= 20)  return { label: `🚨 ${Math.round(minsToHardCutoff)}m left`, color: '#ef4444', pulse: true };
    if (minsToHardCutoff <= 45)  return { label: `⚠️ ${Math.round(minsToHardCutoff)}m`, color: '#f59e0b', pulse: false };
    if (minsToHardCutoff <= 90)  return { label: `🌙 Nightly Stop`, color: '#fcd34d', pulse: false };
    return null;
  })();
  const availableWindowMins = minsToHardCutoff;

  // ── v4.101.0 DRIFT-PROOF MONTH TOTAL ──────────────────────────────────
  // stats.monthlyMinutes accumulates live and can drift LOW (the "-77h?!"
  // scare). The daily log only contains days closed via End Day / midnight —
  // open or crashed days never land there, so log-sum is a LOWER bound only.
  // Truth = max(accumulator, log-sum + today): the log can add missing days
  // (recovered via call-log import), never subtract counted ones.
  // Auto-correction is UPWARD-ONLY — writing down would destroy banked time.
  const monthlyLogSum = React.useMemo(() => {
    const nowD = new Date();
    const todayStr = nowD.toDateString();
    const mKey = `${nowD.getFullYear()}-${nowD.getMonth()}`;
    let past = 0;
    Object.entries(dailyLog || {}).forEach(([ds, mins]) => {
      if (ds === todayStr) return;
      const d = new Date(ds);
      if (Number.isNaN(d.getTime()) || `${d.getFullYear()}-${d.getMonth()}` !== mKey) return;
      past += Number(mins) || 0;
    });
    return Math.round(past + (Number(stats.dailyMinutes) || 0));
  }, [dailyLog, stats.dailyMinutes]);
  const monthlyBanked = Math.max(Number(stats.monthlyMinutes) || 0, monthlyLogSum);

  React.useEffect(() => {
    if ((Number(stats.monthlyMinutes) || 0) < monthlyBanked - 30) {
      updateStat('monthlyMinutes', monthlyBanked);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyBanked]);

  // ── MONTHLY DEFICIT + CATCH-UP PLAN (v4.96.0) ──────────────────────────
  // One source of truth: computeCatchUp owns the pace math now. Duplicating it
  // here is how the HUD and the Goal Tracking view drift apart.
  // v4.101.0: catch-up spreads over WORKDAYS (6.5/Wk → 28d basis), not calendar days.
  // ANCHORED-GOAL: goalSetAt/goalBaseMinutes make the pace clock start the day
  // the goal was banked (a goal set on the 20th is not judged against day 1).
  // Absent for legacy saved stats → exactly the previous full-month pace.
  const catchUpPlan = computeCatchUp({
    goalMinutes: stats.goalMinutes,
    monthlyMinutes: monthlyBanked,
    dailyMinutes: Math.round(totalDailyMins),
    workDays: goalWorkDays,
    goalSetAt: stats.goalSetAt || null,
    goalBaseMinutes: stats.goalBaseMinutes || 0,
  });
  const monthlyDeficitMins = catchUpPlan.deficitMins; // positive = behind
  const isInDeficit = monthlyDeficitMins > 30;
  const catchUpVerdictColor = catchUpPlan.verdict === 'fits-by-18' ? '#34d399'
    : catchUpPlan.verdict === 'needs-ot' ? '#fbbf24'
    : catchUpPlan.verdict === 'impossible' ? '#f87171'
    : '#34d399';
  const catchUpLineColor = catchUpPlan.deficitMins > 30 ? '#fca5a5'
    : catchUpPlan.deficitMins < -30 ? '#6ee7b7'
    : 'var(--text-muted)';

  // ── RHYTHM VIEW DATA (v4.101.0) ────────────────────────────────────────
  // "What rhythm do I need to make the goal?" — actual avg per worked day,
  // projected finish date at that rhythm, and required per-workday load.
  const rhythm = React.useMemo(() => {
    const nowD = new Date();
    const todayStr = nowD.toDateString();
    const mKey = `${nowD.getFullYear()}-${nowD.getMonth()}`;
    let worked = 0, sum = 0;
    Object.entries(dailyLog || {}).forEach(([ds, mins]) => {
      const d = new Date(ds);
      if (Number.isNaN(d.getTime()) || `${d.getFullYear()}-${d.getMonth()}` !== mKey || ds === todayStr) return;
      if ((Number(mins) || 0) > 0) { worked += 1; sum += Number(mins) || 0; }
    });
    if ((Number(stats.dailyMinutes) || 0) > 0) { worked += 1; sum += Number(stats.dailyMinutes) || 0; }
    const avgPerWorked = worked > 0 ? Math.round(sum / worked) : 0;
    // ANCHORED-GOAL: goalMinutes is a month total that includes what is already
    // worked, so "remaining to the goal" stays honest mid-month.
    const remainingGoal = Math.max(0, stats.goalMinutes - monthlyBanked);
    const workdaysNeeded = avgPerWorked > 0 ? Math.ceil(remainingGoal / avgPerWorked) : null;
    // workdays → calendar days via the 6.5/Wk spread (workDays per daysInMonth)
    const calDaysNeeded = (workdaysNeeded != null && goalWorkDays > 0)
      ? Math.ceil(workdaysNeeded * daysInMonth / goalWorkDays)
      : null;
    const finishDate = calDaysNeeded != null
      ? new Date(nowD.getFullYear(), nowD.getMonth(), nowD.getDate() + calDaysNeeded)
      : null;
    const inMonth = finishDate
      ? finishDate.getMonth() === nowD.getMonth() && finishDate.getFullYear() === nowD.getFullYear()
      : false;
    return { avgPerWorked, worked, workdaysNeeded, finishDate, inMonth };
  }, [dailyLog, stats.dailyMinutes, stats.goalMinutes, monthlyBanked, goalWorkDays, daysInMonth]);


  // PACE ETA: predicts when you'll hit today's goal at current earned rate
  const pacePrediction = (() => {
    const remaining = Math.max(0, dailyGoal - totalDailyMins);
    if (remaining <= 0) return { label: '✅ Done!', color: '#10b981', detail: null };
    if (totalDailyMins < 5 || shiftElapsedMins < 10) return { label: '–', color: 'var(--text-muted)', detail: 'Warming up...' };
    const ratePerShiftMin = totalDailyMins / shiftElapsedMins; // live rate
    if (ratePerShiftMin <= 0) return { label: '–', color: 'var(--text-muted)', detail: null };
    const minsToGoal = remaining / ratePerShiftMin;
    const goalTime = new Date(Date.now() + minsToGoal * 60000);
    const hh = goalTime.getHours().toString().padStart(2, '0');
    const mm = goalTime.getMinutes().toString().padStart(2, '0');
    const isBeforeCutoff = goalTime.getHours() < HARD_CUTOFF_HOUR;
    const isBeforeShift = goalTime.getHours() < 18;
    return {
      label: `${hh}:${mm}`,
      color: isBeforeCutoff ? (isBeforeShift ? '#10b981' : '#34d399') : '#ef4444',
      detail: isBeforeCutoff ? 'achievable today' : 'past 20:00 cutoff'
    };
  })();

  // QUALITY: late-start aware pacing score (uses live mins)
  const maxEarnableToday = totalDailyMins + (availableWindowMins * 0.58);
  const qualityScore = (() => {
    if (shiftElapsedMins < 5 || dailyGoal <= 0) return null;
    const sessionWindowMins = shiftElapsedMins + availableWindowMins;
    const idealNow = Math.min(dailyGoal, dailyGoal * (shiftElapsedMins / Math.max(sessionWindowMins, 30)));
    if (idealNow <= 0) return null;
    const pct = Math.round((totalDailyMins / idealNow) * 100);
    const capped = Math.min(150, pct);
    let color = '#ef4444';
    if (capped >= 100) color = '#10b981';
    else if (capped >= 80) color = '#34d399';
    else if (capped >= 60) color = '#f59e0b';
    const goalUnreachable = maxEarnableToday < dailyGoal;
    // Suggest a realistic goal rounded to nearest 5m
    const suggestedGoal = Math.floor(maxEarnableToday / 5) * 5;
    return { pct: capped, color, goalUnreachable, suggestedGoal };
  })();

  // STREAK: past days + live "today on track" indicator
  const streak = stats.streak || 0;
  const todayOnTrack = dailyGoal > 0 && totalDailyMins >= dailyGoal;

  // CALL RATE, EFFECTIVE RATE
  const callsToday = stats.callsToday || 0;
  const avgCallMins = callsToday > 0 ? Math.round(totalDailyMins / Math.max(callsToday, 1)) : 0;
  const effectiveRateArsHr = shiftElapsedMins > 10
    ? Math.round((liveDailyArs / shiftElapsedMins) * 60)
    : null;

  const activeRateArsHr = totalDailyMins > 0
    ? Math.round((liveDailyArs / totalDailyMins) * 60)
    : null;

  const rateOf = (view) => view === 'effective' ? effectiveRateArsHr : activeRateArsHr;

  // Sticky connect/stop bar — attach audio first, start call separately.
  // Sticky connect “flash” helper: for virtual cable we treat `cableStreamReady`
  // as the attach condition; for tab share we keep the legacy `tabStreamReady`.
  const tabNeedsReconnect =
    configuredAudioSourceMode === "virtualCable"
      ? !cableStreamReady
      : !micTestMode && !tabStreamReady;
  const apiKeyMissing = needsUserSuppliedDeepgramKey();

  // Encrypted token presence (vault setup) exists even if the session key isn't unlocked yet.
  const encryptedKeySaved = (() => {
    try {
      return (
        !!localStorage.getItem("dg_cipher") &&
        !!localStorage.getItem("dg_salt") &&
        !!localStorage.getItem("dg_iv")
      );
    } catch (_) {
      return false;
    }
  })();
  const vaultNeedsDecrypt = apiKeyMissing && encryptedKeySaved;
  const apiKeyMissingNoVault = apiKeyMissing && !encryptedKeySaved;

  const offCallStatusLabel = buildOffCallStatusLabel({
    settingsOpen,
    vaultNeedsDecrypt,
    apiKeyMissingNoVault,
    vaultStatus,
    connectionState,
    connectionMessage,
    apiKeyMissing,
    isBreakActive,
    isZombieCall,
    audioAttached,
    tabStreamReady,
    cableStreamReady,
    micTestMode,
    audioSourceMode: configuredAudioSourceMode,
  });

  const connectRequireDoubleTapIndicator = false;

  const connectLabel = vaultNeedsDecrypt
    ? 'Unlock key'
    : isZombieCall
    ? 'Re-attach'
    : audioAttached
        ? 'Start interpreting'
        : micTestMode
          ? 'Connect & start'
          : configuredAudioSourceMode === "virtualCable"
            ? 'Connect & start'
            : 'Connect & start';
  const connectSingleTitle = isZombieCall
    ? 'Re-attach to your call (timer saved)'
    : vaultNeedsDecrypt
      ? 'Saved Deepgram key is locked — open it now, then press Unlock'
      : apiKeyMissingNoVault
        ? 'Connect tab audio — if STT fails, add Deepgram key in Settings (gear)'
      : audioAttached
          ? 'Start interpreting — begin transcription'
          : micTestMode
            ? 'Connect microphone and start interpreting'
            : configuredAudioSourceMode === "virtualCable"
              ? 'Connect virtual cable and start interpreting'
              : 'Connect tab audio and start interpreting';
  const connectDoubleTitle = micTestMode
    ? 'Pick a different microphone'
    : vaultNeedsDecrypt
      ? 'Open the saved-key Unlock form'
      : apiKeyMissingNoVault
        ? 'Enter your Deepgram key in Settings'
      : configuredAudioSourceMode === "virtualCable"
        ? 'Pick a different virtual cable input'
        : 'Pick a different browser tab';

  const connectOnSingle = (() => {
    // CRITICAL STT RECOVERY: a locked saved key must open its one-step fix.
    // Never start audio capture here: it wastes time and ends in an opaque
    // Deepgram failure during a live patient call.
    // v4.153.0: one resolver for header + idle pane (audioAttached now means
    // "Deepgram really transcribing", not just connectionState === connected).
    const intent = resolveConnectIntent({
      vaultNeedsDecrypt,
      apiKeyMissingNoVault,
      isZombieCall,
      audioAttached,
    });
    if (intent === 'open-key-unlock') return () => dispatchOpenDeepgramSettings('connect', 'vault_locked');
    if (intent === 'open-key-settings') return () => dispatchOpenDeepgramSettings('connect', 'bundled_missing');
    if (intent === 'recovery') return onRecovery;
    if (intent === 'attach') return onAttachAudio;
    return onStartCall;
  })();

  const connectOnDouble = (() => {
    const intent = resolveConnectIntent({
      vaultNeedsDecrypt,
      apiKeyMissingNoVault,
      isZombieCall,
      audioAttached,
      doubleTap: true,
    });
    if (intent === 'open-key-unlock') return () => dispatchOpenDeepgramSettings('connect', 'vault_locked');
    if (intent === 'open-key-settings') return () => dispatchOpenDeepgramSettings('connect', 'bundled_missing');
    if (intent === 'recovery') return onRecovery;
    if (intent === 'attach-fresh') return onAttachAudioFresh || onAttachAudio;
    return onConnectAnotherTab;
  })();

  const connectFlash = !isBreakActive && (tabNeedsReconnect || (!audioAttached && !isZombieCall));
  const showEndDayButton = !isActive && stats.dailyMinutes > 0 && !isBreakActive;

  // Listen for Demo Scenarios
  useEffect(() => {
    const handleScenario = (e) => {
      const scenario = e.detail;
      if (scenario === 'call') {
        if (audioAttached) onStartCall();
        else onAttachAudio?.();
      } else if (scenario === 'ui_call') {
        // Dev helper: start the “in call UI” without requiring real tab audio.
        // (Captions will stay empty until real audio is attached.)
        onStartCall?.();
      } else if (scenario === 'goal_hit') {
        handleStop();
        setTimeout(() => {
          setCelebration({ 
            type: 'day', 
            label: 'DAILY GOAL SECURED! 🏆', 
            coins: 100 
          });
          audioEngine.playJackpot?.() || audioEngine.playMetalChest();
        }, 1000);
      } else if (scenario === 'break') {
        handleStartBreak();
      } else if (scenario === 'reset') {
        stopBreak();
        stopSession();
        onStopAudio();
        setCelebration(null);
      }
    };
    window.addEventListener('cat_demo_scenario', handleScenario);
    return () => window.removeEventListener('cat_demo_scenario', handleScenario);
  }, [onAttachAudio, onStartCall, audioAttached, onStopAudio, handleStop, handleStartBreak, stopBreak, stopSession, audioEngine]);

  // v4.99.2: one shared metrics payload for the strip in all three layouts.
  const stripMetrics = buildHeaderStripMetrics({
    stats,
    totalDailyMins,
    dailyGoal,
    monthlyProgressRatio,
    monthlyPendingRatio,
    isMonthlyGoalMet,
    isInDeficit,
    currentIdx,
    milestoneLabels,
    liveDailyArs,
  });

  const renderHeaderMetricsStrip = (expandedFlag, { showBars = true, showQuickRow = true } = {}) => (
    <HeaderMetricsStrip
      {...stripMetrics}
      expanded={expandedFlag}
      onToggleExpand={toggleOffCallMetricsExpanded}
      onBarHover={showProgressBarTooltip}
      onBarLeave={hideMetricTooltip}
      scoreView={scoreView}
      onScoreViewChange={expandedFlag ? handleQuickScoreView : undefined}
      studioView={studioView}
      onCycleWorkspace={expandedFlag ? onCycleWorkspace : undefined}
      showStudioHint={showStudioHint}
      showBars={showBars}
      showQuickRow={showQuickRow}
      onCallMins={totalDailyMins}
      offCallMins={totalOffCallMins}
      // Dead toggle guard: full HUD already shows everything — expanding does nothing.
      showExpandToggle={isCollapsed || offCallScoreboardView || !showExpandedIncome}
      detailShown={!isCollapsed && showExpandedIncome && !offCallScoreboardView}
    />
  );

  // v4.99.2: off-call scoreboard — summary + Less + quick icons live INSIDE the
  // audio chips row (one physical line, scrolls when tight); the body keeps
  // only the 3 thin bars.
  const renderInlineMetricsStrip = (expandedFlag) => (
    <HeaderMetricsStrip
      {...stripMetrics}
      layout="inline"
      expanded={expandedFlag}
      onToggleExpand={toggleOffCallMetricsExpanded}
      scoreView={scoreView}
      onScoreViewChange={expandedFlag ? handleQuickScoreView : undefined}
      studioView={studioView}
      onCycleWorkspace={expandedFlag ? onCycleWorkspace : undefined}
      showStudioHint={showStudioHint}
      showBars={false}
      showQuickRow
      showExpandToggle
      detailShown={false}
    />
  );

  // v4.131.0: the 💵⏱☕ chip off-call — one definition for the meter-only HUD
  // mount. (v4.133.0: the off-call mounts use SessionControlsSticky's copy; the
  // chip no longer rides the audio chips row — see trailingSlot below.)
  const renderTargetsChip = () => (
    <StickyTargetsChip
      dailyMinutes={Math.round(totalDailyMins)}
      monthlyMinutes={monthlyBanked}
      workDays={goalWorkDays}
      breakMinutes={Math.round(liveBreakMins)}
      ratePerMinute={RATE_PER_MINUTE}
      goalMinutes={stats.goalMinutes}
      goalSetAt={stats.goalSetAt || null}
      goalBaseMinutes={stats.goalBaseMinutes || 0}
      onOpenGoalsView={onOpenGoalsView}
    />
  );

  const renderOffCallMetricsBars = () => (
    <HeaderMetricsStrip
      {...stripMetrics}
      barsOnly
      onBarHover={showProgressBarTooltip}
      onBarLeave={hideMetricTooltip}
    />
  );

  // v4.99.2: collapsed off-call body is empty — its summary/Less moved into the
  // chips row via trailingSlot (renderInlineMetricsStrip).
  /**
   * The slim control row under the scoreboard: Min/Std/Full, Notes, Tools,
   * Help, Edit-grid, Collapse, Call detection, Call Focus, plus the number
   * pills (shift, log-off, call rate).
   *
   * v4.168.0: extracted so the off-call scoreboard view can render it too. It
   * was gated behind !offCallScoreboardView, which left every control here
   * with NO entry point in the default off-call view - the pills included, and
   * the log-off time is an off-call number by nature.
   */
  const renderCondensedToolbar = () => (
      <div className="condensed-header-toolbar">
      <div id="controls-left-col" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
        <div id="connection-controls-horizontal" className={`${isActive ? 'active-working-state' : ''}`} style={{ display: 'flex', flexDirection: 'row', gap: '0.35rem', alignItems: 'center' }}>
          <StateIndicators 
            state={isActive ? 'call' : isBreakActive ? 'break' : 'avail'} 
            breakMinutes={stats.dailyBreakMinutes || 0} 
            isZombie={isZombieCall} 
            silenceCount={silenceCount}
          />
          <div style={{ fontSize: '0.6rem', fontWeight: 900, color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.05)', padding: '2px 4px', borderRadius: '3px' }}>
            ⏳{Math.floor(minutesSinceLastBreak)}m
          </div>
          <div id="header-edit-tools-mini" style={{ display: 'flex', gap: '2px' }}>
            <button className="edit-btn-tiny" onClick={() => setTimeEditMode('call')} title="Edit call time">📞</button>
            <button className="edit-btn-tiny" onClick={() => setTimeEditMode('break')} title="Edit break time">☕</button>
          </div>
        </div>
        <div id="left-pills-row" style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
           <CopyPill
             id="pill-shift"
             title="SHIFT"
             copyText={formatHoursMins(shiftElapsedMins)}
             copied={copiedPill}
             onCopy={copyPill}
             style={{ padding: '0.1rem 0.3rem' }}
           >
             <span style={{ fontSize: '0.55rem' }}>🏃{formatHoursMins(shiftElapsedMins)}</span>
           </CopyPill>
           <CopyPill
             id="pill-logoff"
             title="LOG OFF"
             copyText={getCompensatedLogOff()}
             copied={copiedPill}
             onCopy={copyPill}
             style={{
               position: 'relative',
               overflow: 'hidden',
               background: 'rgba(245,158,11,0.08)',
               border: '1px solid rgba(245,158,11,0.2)',
               padding: '0.1rem 0.3rem',
               boxShadow: shouldLogoffShine ? '0 0 14px rgba(245,158,11,0.45)' : undefined,
               animation: shouldLogoffShine ? 'shineSweep 3s linear infinite' : undefined,
               backgroundImage: shouldLogoffShine ? 'linear-gradient(90deg, rgba(245,158,11,0.10), rgba(245,158,11,0.22), rgba(245,158,11,0.10))' : undefined,
               backgroundSize: shouldLogoffShine ? '200% 100%' : undefined,
               backgroundPosition: '0% 0%',
             }}
           >
             <div
               aria-hidden="true"
               style={{
                 position: 'absolute',
                 left: 0,
                 top: 0,
                 bottom: 0,
                 width: `${Math.round(logoffGlowProgress * 100)}%`,
                 background: 'rgba(245,158,11,0.22)',
                 transition: 'width 0.8s ease',
                 pointerEvents: 'none',
               }}
             />
             <span style={{ position: 'relative', zIndex: 1, color: '#fcd34d', fontSize: '0.55rem' }}>
               🚪{getCompensatedLogOff()}
             </span>
           </CopyPill>
        </div>
      </div>

      <div id="controls-right-col" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center', justifyContent: 'flex-end', ...helpStyle }}>
        <HelpLabel text="Tools & Rates" />
        
        {/* Rate Pills */}
        <div id="right-pills-vertical" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '0.2rem', alignItems: 'center' }}>
          {callsToday > 0 ? (
            <CopyPill
              id="pill-call-rate"
              title="CALL METRICS"
              copyText={`${callsToday} calls today, average ${avgCallMins} min per call`}
              copied={copiedPill}
              onCopy={copyPill}
              style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', padding: '0.05rem 0.15rem' }}
            >
              <span style={{ fontSize: '0.55rem', color: '#93c5fd', fontWeight: 700 }}>📞{callsToday}×{avgCallMins}m</span>
            </CopyPill>
          ) : (
            <div id="pill-no-calls" className="metric-pill compact-pill" style={{ opacity: 0.2, padding: '0.05rem 0.15rem', cursor: 'default' }}>
              <span style={{ fontSize: '0.55rem' }}>📞 –</span>
            </div>
          )}
          {effectiveRateArsHr ? (
            <div id="pill-eff-rate" className="metric-pill compact-pill" 
              onClick={() => setRateView(prev => prev === 'effective' ? 'active' : 'effective')}
              title={rateView === 'effective' ? "EFFECTIVE RATE: Hourly wage including dead time. Click to see ACTIVE RATE." : "ACTIVE RATE: Hourly wage during actual calls (Max Rate). Click to see EFFECTIVE RATE."} 
              style={{ background: rateView === 'effective' ? 'rgba(139,92,246,0.08)' : 'rgba(16,185,129,0.08)', border: rateOf(rateView) ? `1px solid ${rateView === 'effective' ? 'rgba(139,92,246,0.2)' : 'rgba(16,185,129,0.2)'}` : 'none', padding: '0.05rem 0.15rem', cursor: 'pointer' }}>
              <span style={{ fontSize: '0.55rem', color: rateView === 'effective' ? '#c4b5fd' : '#10b981', fontWeight: 700 }}>
                {rateView === 'effective' ? '⚡' : '🔥'}${Math.round(rateOf(rateView) / 1000)}k/h
              </span>
            </div>
          ) : (
            <div id="pill-no-rate" className="metric-pill compact-pill" style={{ opacity: 0.2, padding: '0.05rem 0.15rem', cursor: 'default' }}>
              <span style={{ fontSize: '0.55rem' }}>? -</span>
            </div>
          )}
        </div>

        {/* Utility Tool Buttons - Consolidated Row */}
        <div id="right-tool-vertical" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '2px', marginTop: 'auto', justifyContent: 'center', alignItems: 'center' }}>
            {Object.entries(PRESET_LABELS).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className="btn-icon tiny-btn btn-text"
                onClick={() => applyScoreboardPreset(id)}
                style={{
                  fontSize: '0.45rem', fontWeight: 800,
                  opacity: scoreboardPreset === id ? 1 : 0.4,
                  background: scoreboardPreset === id ? 'rgba(239,68,68,0.2)' : 'transparent',
                }}
                title={`Scoreboard: ${label}`}
              >
                {id === 'minimal' ? 'Min' : id === 'standard' ? 'Std' : 'Full'}
              </button>
            ))}
            <button id="header-notes-secondary-btn" className="btn-icon tiny-btn" onClick={toggleQuickNotes} style={{ opacity: isNotesOpen ? 1 : 0.3 }} title="Notes"><NotesIcon size={14} /></button>
            <button id="header-tools-btn" className="btn-icon tiny-btn" onClick={toggleToolbar} style={{ opacity: isToolbarVisible ? 1 : 0.3, background: isToolbarVisible ? 'rgba(239,68,68,0.15)' : 'transparent' }} title="Show tools (notes + background)"><ToolsIcon size={14} /></button>
            <button id="header-help-btn" className="btn-icon tiny-btn" onClick={() => setIsScoreboardHelpVisible(!isScoreboardHelpVisible)} style={{ opacity: isScoreboardHelpVisible ? 1 : 0.3, background: isScoreboardHelpVisible ? 'rgba(239,68,68,0.15)' : 'transparent' }} title="Scoreboard help labels"><HelpIcon size={14} /></button>
            <button id="header-edit-btn" className="btn-icon tiny-btn" onClick={() => { if(isCollapsed) setIsCollapsed(false); setIsEditingScoreboard(!isEditingScoreboard); }} style={{ opacity: isEditingScoreboard ? 1 : 0.3 }} title="Edit Grid"><EditIcon size={14} /></button>
            <button id="header-hud-collapse-btn" className="btn-icon tiny-btn" onClick={() => setIsCollapsed(!isCollapsed)} title={isCollapsed ? "Expand HUD" : "Collapse HUD"}>{isCollapsed ? <ChevronUpIcon size={14} /> : <ChevronDownIcon size={14} />}</button>
            <button id="header-calldetect-btn" className="btn-icon tiny-btn" onClick={() => setIsCallDetectionEnabled(!isCallDetectionEnabled)} style={{ opacity: isCallDetectionEnabled ? 1 : 0.3, background: isCallDetectionEnabled ? 'rgba(16,185,129,0.1)' : 'transparent' }} title="Call Detection">{isCallDetectionEnabled ? <SignalIcon size={14} /> : <SignalOffIcon size={14} />}</button>
            <button id="header-focus-btn" className="btn-icon tiny-btn" onClick={() => setCallFocusMode(!callFocusMode)} style={{ opacity: callFocusMode ? 1 : 0.3, background: callFocusMode ? 'rgba(16,185,129,0.1)' : 'transparent' }} title="Call Focus: auto-hide sidebars during calls">{callFocusMode ? <FocusIcon size={14} /> : <FocusOffIcon size={14} />}</button>
        </div>
      </div>
      </div>
  );

  // v4.99.2 emptied the collapsed off-call body because its summary/Less moved
  // into the chips row. v4.168.0 puts the control row back: the header was 83px
  // against a 132px cap (22vh) and the toolbar is ~30px, so this lands at
  // ~113px - still inside the existing cap. No new cap, no CSS budget change.
  const renderOffCallCollapsedBody = () => (
    <div className="dashboard-header-fill scoreboard-workspace scoreboard-workspace--header">
      <div className="condensed-header-toolbar">
      <div id="controls-left-col" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
        <div id="connection-controls-horizontal" className={`${isActive ? 'active-working-state' : ''}`} style={{ display: 'flex', flexDirection: 'row', gap: '0.35rem', alignItems: 'center' }}>
          <StateIndicators 
            state={isActive ? 'call' : isBreakActive ? 'break' : 'avail'} 
            breakMinutes={stats.dailyBreakMinutes || 0} 
            isZombie={isZombieCall} 
            silenceCount={silenceCount}
          />
          <div style={{ fontSize: '0.6rem', fontWeight: 900, color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.05)', padding: '2px 4px', borderRadius: '3px' }}>
            ⏳{Math.floor(minutesSinceLastBreak)}m
          </div>
          <div id="header-edit-tools-mini" style={{ display: 'flex', gap: '2px' }}>
            <button className="edit-btn-tiny" onClick={() => setTimeEditMode('call')} title="Edit call time">📞</button>
            <button className="edit-btn-tiny" onClick={() => setTimeEditMode('break')} title="Edit break time">☕</button>
          </div>
        </div>
        <div id="left-pills-row" style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
           <CopyPill
             id="pill-shift"
             title="SHIFT"
             copyText={formatHoursMins(shiftElapsedMins)}
             copied={copiedPill}
             onCopy={copyPill}
             style={{ padding: '0.1rem 0.3rem' }}
           >
             <span style={{ fontSize: '0.55rem' }}>🏃{formatHoursMins(shiftElapsedMins)}</span>
           </CopyPill>
           <CopyPill
             id="pill-logoff"
             title="LOG OFF"
             copyText={getCompensatedLogOff()}
             copied={copiedPill}
             onCopy={copyPill}
             style={{
               position: 'relative',
               overflow: 'hidden',
               background: 'rgba(245,158,11,0.08)',
               border: '1px solid rgba(245,158,11,0.2)',
               padding: '0.1rem 0.3rem',
               boxShadow: shouldLogoffShine ? '0 0 14px rgba(245,158,11,0.45)' : undefined,
               animation: shouldLogoffShine ? 'shineSweep 3s linear infinite' : undefined,
               backgroundImage: shouldLogoffShine ? 'linear-gradient(90deg, rgba(245,158,11,0.10), rgba(245,158,11,0.22), rgba(245,158,11,0.10))' : undefined,
               backgroundSize: shouldLogoffShine ? '200% 100%' : undefined,
               backgroundPosition: '0% 0%',
             }}
           >
             <div
               aria-hidden="true"
               style={{
                 position: 'absolute',
                 left: 0,
                 top: 0,
                 bottom: 0,
                 width: `${Math.round(logoffGlowProgress * 100)}%`,
                 background: 'rgba(245,158,11,0.22)',
                 transition: 'width 0.8s ease',
                 pointerEvents: 'none',
               }}
             />
             <span style={{ position: 'relative', zIndex: 1, color: '#fcd34d', fontSize: '0.55rem' }}>
               🚪{getCompensatedLogOff()}
             </span>
           </CopyPill>
        </div>
      </div>

      <div id="controls-right-col" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center', justifyContent: 'flex-end', ...helpStyle }}>
        <HelpLabel text="Tools & Rates" />
        
        {/* Rate Pills */}
        <div id="right-pills-vertical" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '0.2rem', alignItems: 'center' }}>
          {callsToday > 0 ? (
            <CopyPill
              id="pill-call-rate"
              title="CALL METRICS"
              copyText={`${callsToday} calls today, average ${avgCallMins} min per call`}
              copied={copiedPill}
              onCopy={copyPill}
              style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', padding: '0.05rem 0.15rem' }}
            >
              <span style={{ fontSize: '0.55rem', color: '#93c5fd', fontWeight: 700 }}>📞{callsToday}×{avgCallMins}m</span>
            </CopyPill>
          ) : (
            <div id="pill-no-calls" className="metric-pill compact-pill" style={{ opacity: 0.2, padding: '0.05rem 0.15rem', cursor: 'default' }}>
              <span style={{ fontSize: '0.55rem' }}>📞 –</span>
            </div>
          )}
          {effectiveRateArsHr ? (
            <div id="pill-eff-rate" className="metric-pill compact-pill" 
              onClick={() => setRateView(prev => prev === 'effective' ? 'active' : 'effective')}
              title={rateView === 'effective' ? "EFFECTIVE RATE: Hourly wage including dead time. Click to see ACTIVE RATE." : "ACTIVE RATE: Hourly wage during actual calls (Max Rate). Click to see EFFECTIVE RATE."} 
              style={{ background: rateView === 'effective' ? 'rgba(139,92,246,0.08)' : 'rgba(16,185,129,0.08)', border: rateOf(rateView) ? `1px solid ${rateView === 'effective' ? 'rgba(139,92,246,0.2)' : 'rgba(16,185,129,0.2)'}` : 'none', padding: '0.05rem 0.15rem', cursor: 'pointer' }}>
              <span style={{ fontSize: '0.55rem', color: rateView === 'effective' ? '#c4b5fd' : '#10b981', fontWeight: 700 }}>
                {rateView === 'effective' ? '⚡' : '🔥'}${Math.round(rateOf(rateView) / 1000)}k/h
              </span>
            </div>
          ) : (
            <div id="pill-no-rate" className="metric-pill compact-pill" style={{ opacity: 0.2, padding: '0.05rem 0.15rem', cursor: 'default' }}>
              <span style={{ fontSize: '0.55rem' }}>? -</span>
            </div>
          )}
        </div>

        {/* Utility Tool Buttons - Consolidated Row */}
        <div id="right-tool-vertical" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '2px', marginTop: 'auto', justifyContent: 'center', alignItems: 'center' }}>
            {Object.entries(PRESET_LABELS).map(([id, label]) => (
              <button
                key={id}
                type="button"
                className="btn-icon tiny-btn btn-text"
                onClick={() => applyScoreboardPreset(id)}
                style={{
                  fontSize: '0.45rem', fontWeight: 800,
                  opacity: scoreboardPreset === id ? 1 : 0.4,
                  background: scoreboardPreset === id ? 'rgba(239,68,68,0.2)' : 'transparent',
                }}
                title={`Scoreboard: ${label}`}
              >
                {id === 'minimal' ? 'Min' : id === 'standard' ? 'Std' : 'Full'}
              </button>
            ))}
            <button id="header-notes-secondary-btn" className="btn-icon tiny-btn" onClick={toggleQuickNotes} style={{ opacity: isNotesOpen ? 1 : 0.3 }} title="Notes"><NotesIcon size={14} /></button>
            <button id="header-tools-btn" className="btn-icon tiny-btn" onClick={toggleToolbar} style={{ opacity: isToolbarVisible ? 1 : 0.3, background: isToolbarVisible ? 'rgba(239,68,68,0.15)' : 'transparent' }} title="Show tools (notes + background)"><ToolsIcon size={14} /></button>
            <button id="header-help-btn" className="btn-icon tiny-btn" onClick={() => setIsScoreboardHelpVisible(!isScoreboardHelpVisible)} style={{ opacity: isScoreboardHelpVisible ? 1 : 0.3, background: isScoreboardHelpVisible ? 'rgba(239,68,68,0.15)' : 'transparent' }} title="Scoreboard help labels"><HelpIcon size={14} /></button>
            <button id="header-edit-btn" className="btn-icon tiny-btn" onClick={() => { if(isCollapsed) setIsCollapsed(false); setIsEditingScoreboard(!isEditingScoreboard); }} style={{ opacity: isEditingScoreboard ? 1 : 0.3 }} title="Edit Grid"><EditIcon size={14} /></button>
            <button id="header-hud-collapse-btn" className="btn-icon tiny-btn" onClick={() => setIsCollapsed(!isCollapsed)} title={isCollapsed ? "Expand HUD" : "Collapse HUD"}>{isCollapsed ? <ChevronUpIcon size={14} /> : <ChevronDownIcon size={14} />}</button>
            <button id="header-calldetect-btn" className="btn-icon tiny-btn" onClick={() => setIsCallDetectionEnabled(!isCallDetectionEnabled)} style={{ opacity: isCallDetectionEnabled ? 1 : 0.3, background: isCallDetectionEnabled ? 'rgba(16,185,129,0.1)' : 'transparent' }} title="Call Detection">{isCallDetectionEnabled ? <SignalIcon size={14} /> : <SignalOffIcon size={14} />}</button>
            <button id="header-focus-btn" className="btn-icon tiny-btn" onClick={() => setCallFocusMode(!callFocusMode)} style={{ opacity: callFocusMode ? 1 : 0.3, background: callFocusMode ? 'rgba(16,185,129,0.1)' : 'transparent' }} title="Call Focus: auto-hide sidebars during calls">{callFocusMode ? <FocusIcon size={14} /> : <FocusOffIcon size={14} />}</button>
        </div>
      </div>
      </div>
    </div>
  );

  // v4.176.0: the 12-cell grid, extracted so the ▦ toggle can also show it
  // in the compact call header (one copy, two callers).
  const renderNumericGrid = () => (
    <div id="numeric-metric-grid" className="metric-grid metric-grid--scoreboard">
      {isEditingScoreboard && (
        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '3px', flexWrap: 'wrap', padding: '2px 0', alignItems: 'center' }}>
          <span style={{ fontSize: '0.5rem', opacity: 0.5, marginRight: 4 }}>Preset:</span>
          {Object.entries(PRESET_LABELS).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => applyScoreboardPreset(id)}
              style={{
                fontSize: '0.5rem', fontWeight: 700, padding: '1px 5px', borderRadius: 3, cursor: 'pointer',
                background: scoreboardPreset === id ? 'rgba(239,68,68,0.25)' : 'rgba(255,255,255,0.06)',
                border: scoreboardPreset === id ? '1px solid rgba(239,68,68,0.5)' : '1px solid rgba(255,255,255,0.1)',
                color: scoreboardPreset === id ? '#93c5fd' : 'rgba(255,255,255,0.6)',
              }}
              title={`Apply ${label} scoreboard preset`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {/* 1. Mins worked today */}
      {showMetric('m1') && (
      <div
        id="metric-m1"
        className={`metric-cell ${isEditingScoreboard ? 'grid-edit-mode' : ''}`}
        title="Minutes worked today (Click to toggle H:M)"
        style={{ position: 'relative', background: 'rgba(239,68,68,0.06)', cursor: 'pointer' }}
        onClick={() => setShowAsHours(!showAsHours)}
        onMouseEnter={(e) => showMetricTooltip(e, 1)}
        onMouseLeave={hideMetricTooltip}
      >
        <MetricVisibilityToggle metricKey="m1" />
        <HelpLabel text="1. MINS TODAY" />
        <div className="metric-cell-val" style={{ color: '#f87171' }}><StatNumber value={formatLiveMins(totalDailyMins, isActive)} size="lg" format={false} /></div>
        <MetricPct>{metricPcts.minsToday}</MetricPct>
        <div className="metric-cell-label">MINS TODAY</div>
      </div>
      )}

      {showMetric('m2') && (
      <div
        id="metric-m2"
        className={`metric-cell ${isEditingScoreboard ? 'grid-edit-mode' : ''}`}
        title="Minutes left for daily goal (Click to toggle H:M)"
        style={{ position: 'relative', background: 'rgba(239,68,68,0.04)', cursor: 'pointer' }}
        onClick={() => setShowAsHours(!showAsHours)}
        onMouseEnter={(e) => showMetricTooltip(e, 2)}
        onMouseLeave={hideMetricTooltip}
      >
        <MetricVisibilityToggle metricKey="m2" />
        <HelpLabel text="2. LEFT TODAY" />
        <div className="metric-cell-val" style={{ color: '#fca5a5' }}><StatNumber value={formatLiveMins(Math.max(0, dailyGoal - totalDailyMins), isActive)} size="lg" format={false} /></div>
        <MetricPct>{metricPcts.leftToday}</MetricPct>
        <div className="metric-cell-label">LEFT TODAY</div>
      </div>
      )}

      {/* 3. Goal mins */}
      {showMetric('m3') && (
      <div
        id="metric-m3"
        className={`metric-cell ${isEditingScoreboard ? 'grid-edit-mode' : ''}`}
        title="Target goal minutes for today (Click to toggle H:M)"
        style={{ position: 'relative', background: 'rgba(52,211,153,0.04)', cursor: 'pointer' }}
        onClick={() => setShowAsHours(!showAsHours)}
        onMouseEnter={(e) => showMetricTooltip(e, 3)}
        onMouseLeave={hideMetricTooltip}
      >
        <MetricVisibilityToggle metricKey="m3" />
        <HelpLabel text="3. TODAY GOAL" />
        <div className="metric-cell-val"><StatNumber value={formatValue(dailyGoal)} size="lg" format={false} /></div>
        <MetricPct>{metricPcts.todayGoal}</MetricPct>
        <div className="metric-cell-label">TODAY GOAL</div>
      </div>
      )}

      {/* 4. Money today */}
      {showMetric('m4') && (
      <div
        id="metric-m4"
        className={`metric-cell ${isEditingScoreboard ? 'grid-edit-mode' : ''}`}
        title="Money earned today"
        style={{ position: 'relative', background: 'rgba(16,185,129,0.06)' }}
        onMouseEnter={(e) => showMetricTooltip(e, 4)}
        onMouseLeave={hideMetricTooltip}
      >
        <MetricVisibilityToggle metricKey="m4" />
        <HelpLabel text="4. AR$ TODAY" />
        <div className="metric-cell-val" style={{ color: '#34d399' }}>{renderLiveArs(todayArsLive, 'lg', 'AR$')}</div>
        <MetricPct>{metricPcts.arsToday}</MetricPct>
        <div className="metric-cell-label">AR$ TODAY</div>
      </div>
      )}

      {/* 5. Money to be made today */}
      {showMetric('m5') && (
      <div
        id="metric-m5"
        className={`metric-cell ${isEditingScoreboard ? 'grid-edit-mode' : ''}`}
        title="Money remaining for today's goal"
        style={{ position: 'relative', background: 'rgba(245,158,11,0.04)' }}
        onMouseEnter={(e) => showMetricTooltip(e, 5)}
        onMouseLeave={hideMetricTooltip}
      >
        <MetricVisibilityToggle metricKey="m5" />
        <HelpLabel text="5. AR$ LEFT TODAY" />
        <div className="metric-cell-val" style={{ color: '#fcd34d' }}><StatNumber value={cashToTodayGoal} prefix="AR$" size="lg" /></div>
        <MetricPct>{metricPcts.arsLeft}</MetricPct>
        <div className="metric-cell-label">AR$ LEFT TODAY</div>
      </div>
      )}

      {/* 6. Stamina Ratio (On-Call vs Break) */}
      {showMetric('m6') && (
      <div
        id="metric-m6"
        className={`metric-cell ${isEditingScoreboard ? 'grid-edit-mode' : ''}`}
        title="STAMINA RATIO: Your on-call minutes divided by break minutes. Target is 5.3x (8h on / 90m off)."
        style={{ position: 'relative', background: 'rgba(168,85,247,0.04)' }}
        onMouseEnter={(e) => showMetricTooltip(e, 6)}
        onMouseLeave={hideMetricTooltip}
      >
        <MetricVisibilityToggle metricKey="m6" />
        <HelpLabel text="6. STAMINA RATIO" />
        <div className="metric-cell-val" style={{ color: (totalDailyMins / Math.max(0.1, liveBreakMins)) >= 5.3 ? '#c084fc' : '#9ca3af' }}>
          {(totalDailyMins / Math.max(0.1, liveBreakMins)).toFixed(1)}x
        </div>
        <MetricPct>{metricPcts.stamina}</MetricPct>
        <div className="metric-cell-label">STAMINA RATIO</div>
      </div>
      )}

      {/* 7. Money month */}
      {showMetric('m7') && (
      <div
        id="metric-m7"
        className={`metric-cell ${isEditingScoreboard ? 'grid-edit-mode' : ''}`}
        title="Money earned this month"
        style={{ position: 'relative' }}
        onMouseEnter={(e) => showMetricTooltip(e, 7)}
        onMouseLeave={hideMetricTooltip}
      >
        <MetricVisibilityToggle metricKey="m7" />
        <HelpLabel text="7. AR$ MONTH" />
        <div className="metric-cell-val"><StatNumber value={monthlyArs} prefix="AR$" size="lg" /></div>
        <MetricPct>{metricPcts.arsMonth}</MetricPct>
        {/* v4.87.0: monthly minutes visible + one-click editable target */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenGoalsView(); }}
          title={`Month: ${Math.round(stats.monthlyMinutes + unbankedMins)}m of ${stats.goalMinutes}m target — click to open Goal Tracking`}
          style={{
            background: 'rgba(251,146,60,0.12)', border: '1px solid rgba(251,146,60,0.4)',
            borderRadius: 4, color: '#fdba74', cursor: 'pointer',
            fontSize: '0.6rem', fontWeight: 800, padding: '0.1rem 0.35rem',
          }}
        >
          {Math.round(stats.monthlyMinutes + unbankedMins)} / {stats.goalMinutes}m ✎
        </button>
        <div className="metric-cell-label">AR$ MONTH</div>
      </div>
      )}

      {/* 8. Money left month */}
      {showMetric('m8') && (
      <div
        id="metric-m8"
        className={`metric-cell ${isEditingScoreboard ? 'grid-edit-mode' : ''}`}
        title="Money remaining for monthly goal"
        style={{ position: 'relative' }}
        onMouseEnter={(e) => showMetricTooltip(e, 8)}
        onMouseLeave={hideMetricTooltip}
      >
        <MetricVisibilityToggle metricKey="m8" />
        <HelpLabel text="8. AR$ LEFT MONTH" />
        <div className="metric-cell-val"><StatNumber value={Math.max(0, monthlyTargetArs - monthlyArs)} prefix="AR$" size="lg" /></div>
        <MetricPct>{metricPcts.arsLeftMonth}</MetricPct>
        <div className="metric-cell-label">AR$ LEFT MONTH</div>
      </div>
      )}

      {/* 9. Off-call total today */}
      {showMetric('m9') && (
      <div
        id="metric-m9"
        className={`metric-cell ${isEditingScoreboard ? 'grid-edit-mode' : ''}`}
        title="Total time spent off-call today (Click to toggle H:M)"
        style={{ position: 'relative', background: 'rgba(251,146,60,0.06)', cursor: 'pointer' }}
        onClick={() => setShowAsHours(!showAsHours)}
        onMouseEnter={(e) => showMetricTooltip(e, 9)}
        onMouseLeave={hideMetricTooltip}
      >
        <MetricVisibilityToggle metricKey="m9" />
        <HelpLabel text="9. OFF CALL" />
        <div className="metric-cell-val" style={{ color: '#fdba74' }}><StatNumber value={formatLiveMins(totalOffCallMins, !isActive)} size="lg" format={false} /></div>
        <MetricPct>{metricPcts.offCall}</MetricPct>
        <div className="metric-cell-label">OFF CALL</div>
      </div>
      )}

      {/* 10. Avg so far mo */}
      {showMetric('m10') && (
      <div
        id="metric-m10"
        className={`metric-cell ${isEditingScoreboard ? 'grid-edit-mode' : ''}`}
        title="Average minutes per day so far (Click to toggle H:M)"
        style={{ position: 'relative', background: 'rgba(139,92,246,0.04)', cursor: 'pointer' }}
        onClick={() => setShowAsHours(!showAsHours)}
        onMouseEnter={(e) => showMetricTooltip(e, 10)}
        onMouseLeave={hideMetricTooltip}
      >
        <MetricVisibilityToggle metricKey="m10" />
        <HelpLabel text="10. MO AVG" />
        <div className="metric-cell-val"><StatNumber value={formatValue(actualDailyAverage)} size="lg" format={false} /></div>
        <MetricPct>{metricPcts.moAvg}</MetricPct>
        <div className="metric-cell-label">MO AVG</div>
      </div>
      )}

      {/* 11. Silence/Idle Timer */}
      {showMetric('m11') && (
      <div
        id="metric-m11"
        className={`metric-cell ${isEditingScoreboard ? 'grid-edit-mode' : ''}`}
        title="Time since last audio activity (Reset by speech). In call, this tracks patient/user silence."
        style={{ position: 'relative', background: isActive ? 'rgba(239,68,68,0.06)' : 'rgba(255,255,255,0.04)' }}
        onMouseEnter={(e) => showMetricTooltip(e, 11)}
        onMouseLeave={hideMetricTooltip}
      >
        <MetricVisibilityToggle metricKey="m11" />
        <HelpLabel text="11. SILENCE" />
        <div className="metric-watermark">{isActive ? '🔇' : '⏳'}</div>
        <div className="metric-cell-val" style={{ color: silenceCount > 600 ? '#f87171' : 'white' }}>
          <div style={{ visibility: silenceCount > 30 ? 'visible' : 'hidden' }}>
            <StatNumber value={formatTime(silenceCount)} size="md" format={false} />
          </div>
        </div>
        <MetricPct>{metricPcts.silence}</MetricPct>
        <div className="metric-cell-label">{isActive ? 'CALL SILENCE' : 'APP IDLE'}</div>
      </div>
      )}

      {/* 12. Current call min and cash */}
      {showMetric('m12') && (
      <div
        id="metric-m12"
        className={`metric-cell ${isEditingScoreboard ? 'grid-edit-mode' : ''}`}
        title="Current call duration and unbanked cash"
        style={{ position: 'relative', background: isActive ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.02)', border: isActive ? '1px solid rgba(16,185,129,0.3)' : 'none' }}
        onMouseEnter={(e) => showMetricTooltip(e, 12)}
        onMouseLeave={hideMetricTooltip}
      >
        <MetricVisibilityToggle metricKey="m12" />
        <HelpLabel text="12. CURR CALL" />
        <div className="metric-cell-val" style={{ display: 'flex', gap: '0.2rem', alignItems: 'center' }}>
          <StatNumber value={formatTime(sessionSeconds)} size="md" format={false} />
          {renderSessionArs('lg', 'AR$')}
        </div>
        <MetricPct>{metricPcts.currCall}</MetricPct>
        <div className="metric-cell-label">CURR CALL</div>
      </div>
      )}
      <div id="cell-switch-game" className="metric-cell" style={{ cursor: 'pointer', background: 'rgba(255,255,255,0.04)', gridColumn: 'span 3', flexDirection: 'row', minHeight: '26px' }} onClick={() => setScoreView('game')} title="Switch back to gamified view">
        <GameIcon size={14} />
        <div className="metric-cell-label" style={{ opacity: 0.8, marginLeft: '6px' }}>BACK TO GAME VIEW</div>
      </div>
      <div className="metric-cell metric-cell-studio" style={{ gridColumn: 'span 1', minHeight: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }} title="Studio view switch">
        {!isActive && onCycleWorkspace && (
          <WorkspaceViewSwitcher
            view={studioView}
            onCycle={onCycleWorkspace}
            variant="inline"
            showHint={showStudioHint}
          />
        )}
        <AppGuideButton />
      </div>
    </div>
  );

  const renderWorkspaceBody = () => (
      <div className={`dashboard-header-fill${offCallScoreboardView ? ' scoreboard-workspace scoreboard-workspace--header' : ''}`} data-guide={offCallScoreboardView ? 'scoreboard' : undefined}>
      <>
      {/* v4.99.2: off-call expanded — summary/Less/quick live in the chips row
          (trailingSlot); the body only keeps the 3 thin bars. Other views unchanged. */}
      {offCallMetricsExpanded && (
        offCallScoreboardView
          ? renderOffCallMetricsBars()
          : renderHeaderMetricsStrip(true, { showBars: false, showQuickRow: false })
      )}
      {/* COLLAPSED VIEW (hidden when in compact call mode).
          Card also opens via Metrics toggle, but never alongside the expanded income HUD (showExpandedIncome). */}
      {(!isActive || callModeExpanded) && (isCollapsed || (offCallMetricsExpanded && !showExpandedIncome)) && (
        <div className="condensed-header-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.15rem 0.35rem 0', flexWrap: 'wrap' }}>
            {/* v4.96.3: goal pill is off-call chrome — never spend call-mode vertical space on it */}
            {!offCallScoreboardView && !isActive && (
            <button
              type="button"
              className="goal-weekly-pill"
              onClick={onOpenGoalsView}
              title="Weekly commitment — open Goal Tracking view"
              style={{
                background: 'rgba(139, 92, 246, 0.15)',
                border: '1px solid rgba(167, 139, 250, 0.35)',
                borderRadius: 6,
                color: '#c4b5fd',
                cursor: 'pointer',
                fontSize: '0.65rem',
                fontWeight: 800,
                padding: '0.2rem 0.5rem',
              }}
            >
              <TargetIcon size={12} />
              <span>{weeklyGoalHours}h/wk goal</span>
            </button>
            )}
          </div>
          
          {/* Full-width scoreboard (numbers / game flip) */}
          <div id="header-scoreboard-center" data-guide="scoreboard" className="condensed-scoreboard-panel">
            <div className="flip-container scoreboard-flip-container">
              <div className={`flip-card ${scoreView === 'numbers' ? 'is-flipped' : ''}`}>
                
                <div className="flip-front">
                  <GameScoreboard
                    liveDailyArs={liveDailyArs} dailyTargetArs={dailyTargetArs}
                    monthlyArs={monthlyArs} monthlyTargetArs={monthlyTargetArs}
                    stats={stats} dailyGoal={dailyGoal} totalDailyMins={totalDailyMins}
                    goalDayMin={computeGoalDay({ dailyMinutes: stats.dailyMinutes, monthlyMinutes: stats.monthlyMinutes, ratePerMinute: RATE_PER_MINUTE }).dailyMin}
                    totalOffCallMins={totalOffCallMins}
                    shiftElapsedMins={shiftElapsedMins}
                    pacePrediction={pacePrediction} qualityScore={qualityScore} cutoffWarning={cutoffWarning}
                    breakLeft={breakLeft} breakLimit={breakLimit}
                    nextGoalLabel={nextGoalLabel} nextMilestone={nextMilestone}
                    daysInMonth={daysInMonth} currentDay={currentDay} remainingDays={remainingDays}
                    isActive={isActive} isBreakActive={isBreakActive}
                    isZombieCall={isZombieCall}
                    connectionState={connectionState}
                    connectProgress={connectProgress}
                    onAttachAudio={onAttachAudio}
                    onAttachAudioFresh={onAttachAudioFresh}
                    onStartCall={onStartCall}
                    onRecovery={onRecovery}
                    onConnectAnotherTab={onConnectAnotherTab}
                    audioAttached={audioAttached}
                    onSwitchToNumbers={() => setScoreView('numbers')}
                    milestoneTargets={milestoneTargets}
                    isEditingScoreboard={isEditingScoreboard}
                    getCompensatedLogOff={getCompensatedLogOff}
                    connectInHeader={offCallScoreboardView}
                    compactPane={offCallScoreboardView}
                    offCallStatusLabel={offCallStatusLabel}
                    micTestMode={micTestMode}
                    audioSourceMode={configuredAudioSourceMode}
                  />
                </div>

                <div className="flip-back">
                  {showNumericGrid ? (
                  renderNumericGrid()
                  ) : (
                    <div style={{ padding: '0.5rem', fontSize: '0.6rem', opacity: 0.45, textAlign: 'center' }}>
                      Number grid hidden — Settings → Display
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>

          {/* v4.168.0: the toolbar renders in the off-call scoreboard view too, so
              Min/Std/Full, Notes, Tools, Help, Edit-grid, Call detection, Call
              Focus and the copyable pills are reachable where you look at the
              scoreboard. Extracted to renderCondensedToolbar() so both callers
              share one copy. */}
          {renderCondensedToolbar()}
        </div>
      )}

      {/* ── EXPANDED TWO-ROW DASHBOARD ── */}
      {(!isActive || callModeExpanded) && !isCollapsed && showExpandedIncome && (
        <div className={`income-dashboard${isActive ? ' income-dashboard--on-call' : ''}`}>
          
          {/* UPPER ROW: High-Level Progress & The Bounty */}
          <div className="dashboard-row dashboard-row-upper">
            
            {/* Today's Bounty (THE STAR) */}
            <div className="income-card" style={{ flex: '2 1 0', minWidth: 0, background: 'rgba(52, 211, 153, 0.1)', border: '1px solid rgba(52, 211, 153, 0.2)', padding: '0.3rem 0.4rem', borderRadius: '10px', ...helpStyle }} title={`TODAY'S BOUNTY: The remaining AR$ you need to earn today to hit your personalized daily goal (Target: AR$${dailyTargetArs.toLocaleString('es-AR')}).`}>
              <HelpLabel text="Bounty" />
              <span className="income-label" style={{ color: '#6ee7b7', fontWeight: 800 }}>💰 TODAY'S BOUNTY</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                <div style={{ 
                  fontSize: '1.1rem', 
                  fontWeight: 900, 
                  color: isBountyAnimating ? '#fcd34d' : '#fff',
                  transition: 'color 0.3s ease',
                  textShadow: isBountyAnimating ? '0 0 15px rgba(252, 211, 77, 0.5)' : 'none',
                  whiteSpace: 'nowrap'
                }}>
                  <StatNumber value={displayBounty} prefix="AR$" size="xl" />
                </div>
                {isBountyAnimating && <span style={{ fontSize: '0.7rem', color: '#6ee7b7', animation: 'slideUpBounce 0.5s' }}>-tick</span>}
              </div>
              <span style={{ fontSize: '0.55rem', opacity: 0.6, whiteSpace: 'nowrap' }}>Target: AR${dailyTargetArs.toLocaleString('es-AR')}</span>
            </div>

            {/* Monthly Profit */}
            {(isEditingScoreboard || visibleCards.month) && (
              <div className="income-card income-tier-1" style={{ flex: '1 1 0', minWidth: 0, position: 'relative' }} title="MONTHLY PROFIT: Your total banked earnings for the month against your ultimate monthly target.">
                <CardVisibilityToggle cardKey="month" />
                <span className="income-label">🗓️ MO.PROFIT</span>
                <span className="income-ars" style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                  <span>🌊</span>
                  <StatNumber value={monthlyArs} prefix="AR$" size="lg" />
                </span>
                <span style={{ fontSize: '0.55rem', opacity: 0.5, whiteSpace: 'nowrap', display: 'flex', gap: '0.1rem' }}>
                  <span>/</span>
                  <StatNumber value={monthlyTargetArs} prefix="AR$" size="xs" />
                </span>
              </div>
            )}

            {/* Today's Shift Progress */}
            {(isEditingScoreboard || visibleCards.today) && (
              <div className="income-card income-tier-2" style={{ flex: '1 1 0', minWidth: 0, cursor: 'pointer', position: 'relative', ...helpStyle }} onClick={() => !isEditingScoreboard && onOpenGoalsView()} title="DAILY PROGRESS: Minutes banked today out of your total daily target minutes required based on your monthly pacing. Click → Goal Tracking view.">
                <CardVisibilityToggle cardKey="today" />
                <HelpLabel text="Daily Mins" />
                <span className="income-label">{activeDayEmoji} DAILY</span>
                <span className="income-ars" style={{ whiteSpace: 'nowrap' }}>🌊{Math.round(stats.dailyMinutes)}m/🎯{Math.round(dailyGoal)}m</span>
                <span style={{ fontSize: '0.55rem', opacity: 0.6 }}>({(stats.dailyMinutes / (dailyGoal || 1) * 100).toFixed(0)}%)</span>
              </div>
            )}

            {/* Goal Ladder */}
            <div className="income-card" style={{ flex: '1 1 0', minWidth: 0, borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '0.5rem', ...helpStyle }} title={`PRO LADDER: Your next immediate target on the 12-step ladder. Reaching ${nextMilestone}m levels you up!`}>
               <HelpLabel text="Ladder" />
               <span className="income-label">🪜 NEXT</span>
               <span style={{ fontSize: '0.8rem', fontWeight: 800, color: '#a855f7', whiteSpace: 'nowrap' }}>{nextGoalLabel}</span>
               <span style={{ fontSize: '0.55rem', opacity: 0.6, whiteSpace: 'nowrap' }}>{nextMilestone}m</span>
            </div>
          </div>

          {/* MIDDLE ROW: Smart Intelligence Metrics */}
          <div style={{ display: 'flex', gap: '0.4rem', padding: '0.2rem 0', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>

            {/* PACE ETA */}
            <div className="income-card" style={{ flex: '1 1 0', minWidth: 0, background: 'rgba(239,68,68,0.06)' }}
              title={`PACE ETA: Predicts the exact clock time you will hit your daily goal if you maintain your current rate of earning minutes. Currently projecting ${pacePrediction.label}. ${pacePrediction.detail || ''}`}>
              <span className="income-label">🎯 PACE ETA</span>
              <span style={{ fontSize: '1rem', fontWeight: 800, color: pacePrediction.color }}>{pacePrediction.label}</span>
              <span style={{ fontSize: '0.5rem', opacity: 0.5 }}>{pacePrediction.detail || 'at current rate'}</span>
            </div>

            {/* QUALITY */}
            <div className="income-card" style={{ flex: '1 1 0', minWidth: 0, background: 'rgba(239,68,68,0.06)' }}
              title={qualityScore?.goalUnreachable
                ? `DAY QUALITY: Goal (${Math.round(dailyGoal)}m) unreachable before midnight cutoff. Suggested adaptive realistic goal: ${qualityScore.suggestedGoal}m.`
                : qualityScore ? `DAY QUALITY: ${qualityScore.pct}% of your ideal required pace for your current session window. Keep it near 100%!` : 'DAY QUALITY: Not enough data yet to calculate pacing quality.'}>
              <span className="income-label">📈 QUALITY</span>
              <span style={{ fontSize: '1rem', fontWeight: 800, color: qualityScore?.goalUnreachable ? '#f59e0b' : (qualityScore?.color || 'var(--text-muted)') }}>
                {qualityScore?.goalUnreachable ? `→ ${qualityScore.suggestedGoal}m` : qualityScore ? `${qualityScore.pct}%` : '–'}
              </span>
              <span style={{ fontSize: '0.5rem', opacity: 0.5 }}>
                {qualityScore?.goalUnreachable ? 'adapt today\'s goal' : 'vs ideal pace'}
              </span>
            </div>

            {/* STREAK */}
            <div className="income-card" style={{ flex: '1 1 0', minWidth: 0, background: todayOnTrack ? 'rgba(16,185,129,0.08)' : 'rgba(251,146,60,0.06)' }}
              title={`STREAK: You have hit your goal for ${streak} consecutive past days. ${todayOnTrack ? "You have already hit today's goal! (+1 day added to streak at midnight)" : "Today is still in progress."}`}>
              <span className="income-label">🔥 STREAK</span>
              <span style={{ fontSize: '1rem', fontWeight: 800, color: todayOnTrack ? '#10b981' : streak >= 3 ? '#fb923c' : streak > 0 ? '#fcd34d' : 'var(--text-muted)' }}>
                {streak > 0 ? `${streak}d` : ''}{todayOnTrack ? (streak > 0 ? '+today ✅' : 'today ✅') : streak === 0 ? 'none yet' : ''}
              </span>
              <span style={{ fontSize: '0.5rem', opacity: 0.5 }}>{todayOnTrack ? 'goal already hit!' : 'past days at goal'}</span>
            </div>

            {/* CALL RATE */}
            <div className="income-card" style={{ flex: '1 1 0', minWidth: 0, background: 'rgba(239,68,68,0.06)' }}
              title={`CALLS: You have taken ${callsToday} calls today, with an average duration of ${avgCallMins} minutes each.`}>
              <span className="income-label">📞 CALLS</span>
              <span style={{ fontSize: '1rem', fontWeight: 800, color: callsToday > 0 ? '#93c5fd' : 'var(--text-muted)' }}>
                {callsToday > 0 ? `${callsToday}×${avgCallMins}m` : '–'}
              </span>
              <span style={{ fontSize: '0.5rem', opacity: 0.5 }}>count × avg mins</span>
            </div>

            {/* EFFECTIVE RATE */}
            <div className="income-card" style={{ flex: '1 1 0', minWidth: 0, background: 'rgba(139,92,246,0.06)' }}
              title={`EFFECTIVE RATE: AR$${effectiveRateArsHr?.toLocaleString('es-AR') || '–'}/hr. This represents your true hourly wage today, factoring in both active call time and unpaid waiting time (Avail).`}>
              <span className="income-label">⚡ EFF. RATE</span>
              <span style={{ fontSize: '1rem', fontWeight: 800, color: effectiveRateArsHr ? '#c4b5fd' : 'var(--text-muted)' }}>
                {effectiveRateArsHr ? `AR$${effectiveRateArsHr.toLocaleString('es-AR')}/h` : '–'}
              </span>
              <span style={{ fontSize: '0.5rem', opacity: 0.5 }}>incl. avail time</span>
            </div>

            {/* BREAK BUDGET */}
            {(isEditingScoreboard || visibleCards.break) && (
            <div className="income-card" style={{ flex: '1 1 0', minWidth: 0, position: 'relative', background: breakLeft < 15 ? 'rgba(239,68,68,0.06)' : 'rgba(251,146,60,0.06)' }}
              title={`BREAK BUDGET: You have ${Math.round(breakLeft)} minutes left of your ${breakLimit}-minute daily coffee break allowance.`}>
              <CardVisibilityToggle cardKey="break" />
              <span className="income-label">☕ BREAK LEFT</span>
              <span style={{ fontSize: '1rem', fontWeight: 800, color: breakLeft < 15 ? '#ef4444' : breakLeft < 30 ? '#f59e0b' : '#6ee7b7' }}>
                {Math.round(breakLeft)}m
              </span>
              <span style={{ fontSize: '0.5rem', opacity: 0.5 }}>of {breakLimit}m budget</span>
            </div>
            )}

          </div>

          {/* LOWER ROW: Interaction & Live Metrics (no duplicate STOP/HOLD — sticky bar owns those) */}
          <div className="dashboard-row dashboard-row-lower">
            
            {/* Main Controls Group */}
            <div className="header-utility-inline" style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
              {!isActive && (
                <StateIndicators state={isBreakActive ? 'break' : 'avail'} breakMinutes={stats.dailyBreakMinutes || 0} />
              )}
              {isBreakActive ? (
                <button id="stop-break-btn" className="btn" onClick={stopBreak} style={{ background: '#fb923c', color: 'white' }}>STOP BREAK</button>
              ) : (
                <button id="break-btn" className="btn" onClick={startBreak} disabled={isActive} style={{ opacity: isActive ? 0.3 : 1 }}>COFFEE</button>
              )}
              
              <div className="header-utility-group" style={{ display: 'flex', gap: '0.2rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn-icon-tiny" onClick={() => setTimeEditMode('call')} title="Edit call time">✏️📞</button>
                <button className="btn-icon-tiny" onClick={() => setTimeEditMode('break')} title="Edit break time">✏️☕</button>
                <button className={`btn-icon-tiny ${isNotesOpen ? 'active' : ''}`} onClick={toggleQuickNotes} title="Notes">📝</button>
                <button className={`btn-icon-tiny ${isToolbarVisible ? 'active' : ''}`} onClick={toggleToolbar} title="Show tools (notes + background)">🛠️</button>
                <button className={`btn-icon-tiny ${isEditingScoreboard ? 'active' : ''}`} onClick={() => setIsEditingScoreboard(!isEditingScoreboard)} title="Edit Grid">{isEditingScoreboard ? '💾' : '✏️'}</button>
                <button className={`btn-icon-tiny ${isScoreboardHelpVisible ? 'active' : ''}`} onClick={() => setIsScoreboardHelpVisible(!isScoreboardHelpVisible)} title="Help">❓</button>
                <button className="btn-icon-tiny" onClick={onOpenGoalsView} title="Goal Tracking — month calendar">📅</button>
                <button className="btn-icon-tiny danger" onClick={() => setIsCollapsed(true)} title="Collapse">▲</button>
              </div>
            </div>

            {/* Current Call (Live) — hidden during active call (sticky bar shows timer) */}
            {(isEditingScoreboard || visibleCards.call) && !isActive && (
              <div className={`income-card ${isActive ? 'active' : ''}`} style={{ flex: '1 1 0', minWidth: 0, position: 'relative', borderLeft: '1px solid rgba(255,255,255,0.1)', paddingLeft: '0.5rem', ...helpStyle }} title="CURRENT CALL: Active duration and unbanked earnings of the ongoing call.">
                <CardVisibilityToggle cardKey="call" />
                <HelpLabel text="Call" />
                <span className="income-label" style={{ fontSize: '0.55rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>CALL ({formatTime(sessionSeconds)})</span>
                </span>
                <span className="income-ars" style={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                  {renderSessionArs('sm')}
                </span>
              </div>
            )}

            {/* Audio Sinks — off-call only (I/O strip in sticky header during calls) */}
            {!isActive && (
            <div className="dashboard-audio-sinks" style={{ display: 'flex', gap: '0.2rem', alignItems: 'center', flexShrink: 1, minWidth: 0 }}>
              <select
                className="btn"
                style={{ fontSize: '0.6rem', maxWidth: '80px', minWidth: '50px', flex: '1 1 0' }}
                value={selectedMicId}
                onChange={(e) => changeMicId(e.target.value)}
                onFocus={() => fetchDevices({ requestMicPermissionForLabels: true })}
              >
                <option value="">🎤 Mic</option>
                {inputDevices.map(d => <option key={d.deviceId} value={d.deviceId}>{d.label || `Mic ${d.deviceId.slice(0,5)}`}</option>)}
              </select>
              <select
                className="btn"
                style={{ fontSize: '0.6rem', maxWidth: '80px', minWidth: '50px', flex: '1 1 0' }}
                value={selectedSinkId}
                onChange={(e) => changeSinkId(e.target.value)}
                onFocus={() => fetchDevices({ requestMicPermissionForLabels: false })}
              >
                <option value="">🔊 Spk</option>
                {outputDevices.map((d, i) => <option key={d.deviceId} value={d.deviceId} title={d.label || undefined}>{displayDeviceName(d, i, 'out', null, outputDevices)}</option>)}
              </select>
            </div>
            )}

            {/* Shift Recovery Stats */}
            <div className="dashboard-shift-stats" style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', borderLeft: isActive ? 'none' : '1px solid rgba(255,255,255,0.1)', paddingLeft: isActive ? 0 : '0.4rem', marginLeft: isActive ? 0 : 'auto', flexWrap: 'wrap', minWidth: 0 }}>
              <div className="income-card" title="SHIFT LATE: Minutes past 9:00 AM you first connected today.">
                <span className="income-label">🕒 LATE</span>
                <span style={{ fontSize: '0.8rem', color: (stats.shiftStartSentiment || 0) > 30 ? '#ef4444' : '#6ee7b7' }}>{Math.round(stats.shiftStartSentiment || 0)}m</span>
              </div>
              <div className="income-card" title="ESTIMATED LOG OFF: 18:00 + late arrival + breaks.">
                <span className="income-label">🚪LOGOUT</span>
                <span style={{ fontSize: '0.8rem', fontWeight: 800, color: '#fcd34d' }}>{getCompensatedLogOff()}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress bars */}
      {(!isActive || callModeExpanded) && dailyGoal > 0 && showProgressStack && !offCallScoreboardView && (
        <div className="header-progress-stack" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', padding: '0.25rem 0.15rem 0.1rem' }}>
          {/* Monthly bar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: 'var(--text-muted)', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>🗓️ Day {currentDay}/{daysInMonth}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                {!isMonthlyGoalMet ? (
                  <>
                    <span 
                      title={`PRO LADDER: Step ${currentIdx+1} of 12. Next target is ${nextMilestone}m. Reaching this unlocks richer sounds and levels up your status!`}
                      style={{ color: '#fff', background: 'rgba(239,68,68,0.3)', padding: '0.1rem 0.4rem', borderRadius: '4px', border: '1px solid rgba(239,68,68,0.4)', fontWeight: 800, cursor: 'help' }}>
                       🪜 {nextGoalLabel} ({nextMilestone}m)
                    </span>
                    <span 
                      title={`MONTHLY PROGRESS: ${Math.round(stats.monthlyMinutes)}m banked out of ${stats.goalMinutes}m target. 
You are ${((stats.monthlyMinutes / stats.goalMinutes) * 100).toFixed(1)}% through your goal.
${isInDeficit ? `⚠️ DEFICIT: Behind pace by ${Math.round(monthlyDeficitMins)}m.` : `✅ ON PACE: Ahead of projected daily average.`}`}
                      style={{ margin: '0 0.4rem', fontSize: '0.75rem', color: isMonthlyGoalMet ? '#10b981' : (isInDeficit ? '#f59e0b' : '#a855f7'), fontWeight: 800, cursor: 'help' }}>
                      {((stats.monthlyMinutes / (stats.goalMinutes || 1)) * 100).toFixed(1)}%
                    </span>
                    <span style={{ opacity: 0.4 }}>|</span>
                    <span 
                      title={`PACED MAX: If you keep working ${Math.round(dailyGoal)}m every day for the rest of the month, you are on track to bank AR$${monthlyMaxArs} total. Target is AR$${monthlyTargetArs.toLocaleString('es-AR')}.`}
                      style={{ background: 'rgba(139,92,246,0.15)', padding: '0.1rem 0.4rem', borderRadius: '4px', border: '1px solid rgba(139,92,246,0.3)', cursor: 'help' }}>
                      Paced Max: <strong style={{ color: '#d8b4fe', textShadow: '0 0 8px rgba(139,92,246,0.5)', display: 'inline-flex', alignItems: 'center' }}>
                        <StatNumber value={monthlyRemainingCashVal + monthlyArs} prefix="AR$" size="xs" />
                      </strong>
                    </span>
                  </>
                ) : (
                  <span style={{ color: stats.monthlyMinutes > stats.goalMinutes * 1.2 ? '#fcd34d' : '#34d399', fontWeight: 800 }}>
                    {stats.monthlyMinutes > milestones[11] ? '👑 LEGENDARY STATUS REACHED!' : stats.monthlyMinutes > stats.goalMinutes * 1.2 ? '🔥 UNSTOPPABLE!' : stats.monthlyMinutes > stats.goalMinutes * 1.1 ? '🚀 ORBIT (110%!)' : '🎉 Goal Met!'}
                  </span>
                )}
              </div>
              <span style={{ opacity: 0.5 }}>Goal: {stats.goalMinutes}m</span>
            </div>
            <div
              style={{ position: 'relative', marginTop: '0.3rem' }}
              onMouseEnter={(e) =>
                showProgressBarTooltip(e, {
                  icon: '🗓️',
                  heading: 'MONTHLY PROGRESS',
                  color: isMonthlyGoalMet ? '#10b981' : (isInDeficit ? '#f59e0b' : '#a855f7'),
                  body: `Banked: ${Math.round(stats.monthlyMinutes)}m / ${Math.round(stats.goalMinutes)}m.\n` +
                    `Including current call: ${Math.round(stats.monthlyMinutes + unbankedMins)}m.\n` +
                    `Progress: ${((monthlyProgressRatio || 0) * 100).toFixed(1)}% (pending: ${((monthlyPendingRatio || 0) * 100).toFixed(1)}%).`
                })
              }
              onMouseLeave={hideMetricTooltip}
            >
              <div style={{ height: '7px', background: 'rgba(0,0,0,0.5)', borderRadius: '4px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${monthlyPendingRatio * 100}%`, backgroundColor: '#f97316', opacity: 0.9, transition: 'width 1s linear', zIndex: 1, boxShadow: unbankedMins > 0 ? '0 0 10px #f97316' : 'none' }} />
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${monthlyProgressRatio * 100}%`, backgroundColor: isMonthlyGoalMet ? '#10b981' : '#a855f7', transition: 'width 1s cubic-bezier(0.34, 1.56, 0.64, 1)', zIndex: 2 }} />
              {stats.monthlyMinutes > stats.goalMinutes && <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(1, (stats.monthlyMinutes - stats.goalMinutes) / (stats.goalMinutes * 0.2)) * 100}%`, backgroundColor: 'rgba(245,158,11,0.8)', zIndex: 3 }} />}
              
              {/* Milestone Indicators (Checkpoints at 5.5k, 11k, 16.5k) */}
              <div style={{ position: 'absolute', inset: 0, display: 'flex', pointerEvents: 'none', zIndex: 4 }}>
                {[5500, 11000, 16500].map((m, i) => {
                  const ratio = m / 16500;
                  return (
                    <div 
                      key={m} 
                      title={`Monthly Rank: ${['Floor (5.5k)', 'Growth (11k)', 'Legend (16.5k)'][i]}`}
                      style={{ 
                        position: 'absolute', left: `${ratio * 100}%`, top: 0, bottom: 0, width: '1px', 
                        background: 'rgba(255,255,255,0.6)',
                        boxShadow: '0 0 4px white',
                        pointerEvents: 'auto', cursor: 'help'
                      }}>
                    </div>
                  );
                })}
              </div>

              {/* 500min Nudges (8h shifts) overlay */}
              <div style={{ position: 'absolute', inset: 0, display: 'block', pointerEvents: 'none', zIndex: 4 }}>
                {Array.from({ length: Math.max(0, Math.floor((stats.goalMinutes || 16500) / 500)) }).map((_, i) => {
                  const m = (i + 1) * 500;
                  const ratio = m / (stats.goalMinutes || 16500);
                  if (ratio >= 1) return null;
                  
                  // Calculate absolute distance to the NEXT milestone on the Pro Ladder (steps of 1375)
                  const nextMilestoneAbsolute = Math.ceil((m + 1) / 1375) * 1375;
                  const minsToNextMilestone = nextMilestoneAbsolute - m;
                  const shiftsToNextMilestone = (minsToNextMilestone / 500).toFixed(1);

                  return (
                    <div 
                      key={m} 
                      title={`Shift Checkpoint: ${m}m. You are ${shiftsToNextMilestone} shifts (${minsToNextMilestone}m) away from the next Ladder Milestone (${nextMilestoneAbsolute}m).`}
                      style={{ 
                        position: 'absolute', left: `${ratio * 100}%`, top: 0, bottom: 0, width: '1px', 
                        background: 'rgba(239,68,68,0.5)',
                        pointerEvents: 'auto', cursor: 'help'
                      }}>
                    </div>
                  );
                })}
              </div>

              {/* Day Notches overlay */}
              <div 
                title="Each vertical notch represents one day of the month. The thick white line is TODAY."
                style={{ position: 'absolute', inset: 0, display: 'flex', pointerEvents: 'auto', zIndex: 5, cursor: 'help' }}>
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const d = i + 1;
                  const dateObj = new Date(year, month, d);
                  const dStr = dateObj.toDateString();
                  const isToday = d === currentDay;
                  
                  return (
                    <div key={i} style={{ 
                      flex: 1, position: 'relative', 
                      borderRight: i < daysInMonth - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                      overflow: 'hidden'
                    }}>
                      <MiniDayTimeline 
                        dateStr={dStr} 
                        currentTimeline={isToday ? dailyTimeline : null}
                        dailyMins={isToday ? totalDailyMins : (dailyLog[dStr] || 0)}
                        goalMins={dailyGoal}
                      />
                    </div>
                  );
                })}
              </div>

              <div 
                title={`Today is Day ${currentDay}. Stay ahead of this line to keep your pace!`}
                style={{ position: 'absolute', top: 0, bottom: 0, left: `${monthElapsedRatio * 100}%`, width: '2px', backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10, cursor: 'help', pointerEvents: 'auto' }} />
            </div>
          </div>
          </div>

          {/* v4.96.0: Catch-up strip — behind? → today's number → rest-of-month split → verdict */}
          <div
            title={`CATCH-UP PLAN: expected ${Math.round(catchUpPlan.expectedByToday)}m banked by today · you have ${Math.round(monthlyBanked)}m.\nToday's target: ${catchUpPlan.requiredToday}m on call (${catchUpPlan.remainingWorkdays} workdays left @ ${goalWorkDays}d/mo).\nRest of month: ${catchUpPlan.thenPerDay}m/workday for ${catchUpPlan.daysAfter} more workdays.`}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.5rem', fontSize: '0.62rem', fontWeight: 700, cursor: 'help' }}
          >
            <span style={{ color: catchUpLineColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {formatCatchUpLine(catchUpPlan)}
            </span>
            <span style={{ color: catchUpVerdictColor, whiteSpace: 'nowrap' }}>
              {formatCatchUpVerdict(catchUpPlan)}
              {catchUpPlan.verdict === 'impossible' && qualityScore?.goalUnreachable && (
                <span style={{ opacity: 0.85 }}> → adapt to {qualityScore.suggestedGoal}m</span>
              )}
            </span>
            <button
              type="button"
              onClick={() => setRhythmOpen(o => !o)}
              aria-expanded={rhythmOpen}
              title="Rhythm & schedule: what pace per workday makes the goal"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#e2e8f0', borderRadius: '4px', padding: '0 0.35rem', fontSize: '0.58rem', fontWeight: 800, cursor: 'pointer', lineHeight: 1.4 }}
            >🎵{rhythmOpen ? '▾' : '▸'}</button>
          </div>

          {/* v4.101.0: RHYTHM PANEL — required rhythm vs actual rhythm, projected finish */}
          {rhythmOpen && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', padding: '0.4rem 0.5rem', borderRadius: '6px', background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.08)', fontSize: '0.6rem', color: 'var(--text-muted)' }}>
              <span>
                🎯 <strong style={{ color: '#fff' }}>{fmtHm(catchUpPlan.requiredToday)}/workday</strong> needed
                {' '}({catchUpPlan.remainingWorkdays} workdays left · {goalWorkDays}d/mo @ {daysPerWeekOf(goalWorkDays)}/wk · after today: {fmtHm(catchUpPlan.thenPerDay)})
              </span>
              <span>
                📊 actual: <strong style={{ color: '#fff' }}>{rhythm.avgPerWorked > 0 ? `${fmtHm(rhythm.avgPerWorked)}/worked day` : '—'}</strong>
                {' '}over {rhythm.worked} worked day{rhythm.worked === 1 ? '' : 's'} this month
              </span>
              <span style={{ color: rhythm.finishDate ? (rhythm.inMonth ? '#34d399' : '#f87171') : 'inherit' }}>
                {rhythm.finishDate
                  ? (rhythm.inMonth
                    ? `🏁 at this rhythm you hit ${fmtHm(stats.goalMinutes)} around ${rhythm.finishDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} — inside the month ✅`
                    : `⚠️ at this rhythm you'd only finish ${rhythm.finishDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} — raise to ${fmtHm(catchUpPlan.requiredToday)}/workday`)
                  : '📊 work a day to project your finish date'}
              </span>
              <span style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', opacity: 0.85 }}>
                <span>🗓️ wk rhythm:</span>
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => (
                  <span key={i} title={i < 6 ? 'full workday' : 'half day (6.5/Wk)'} style={{
                    width: '1.1rem', height: '1.1rem', borderRadius: '3px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: '0.55rem',
                    background: i < 6 ? 'rgba(56,189,248,0.25)' : 'rgba(251,191,36,0.18)',
                    border: `1px solid ${i < 6 ? 'rgba(56,189,248,0.5)' : 'rgba(251,191,36,0.4)'}`,
                    color: i < 6 ? '#7dd3fc' : '#fde68a',
                  }}>{i < 6 ? day : `${day}½`}</span>
                ))}
                {/* ANCHORED-GOAL follow-up: the COMMITMENT per workday, not the
                    prorated month total ÷ workdays (which read 152m for a 420m goal). */}
                {/* ANCHORED-GOAL follow-up: the COMMITMENT per workday — the month
                    total ÷ workdays read 152m for a 420m/d (35h/Wk) commitment. */}
                <span style={{ opacity: 0.7 }}>({fmtHm(committedPerWorkday)}/d target)</span>
              </span>
            </div>
          )}

          {/* Step Goal (Weekly Replenishing Bar) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: 'var(--text-muted)', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ fontWeight: 600 }}>🪜 STEP {currentIdx + 1}/12 (1 WEEK OF MINIMUM WORK)</span>
                <span style={{ background: 'rgba(255,255,255,0.1)', padding: '0.1rem 0.3rem', borderRadius: '4px', color: '#fff', fontSize: '0.55rem', fontWeight: 800 }}>
                  🗓️ Day {currentDay} (Week {Math.ceil(currentDay / 7)})
                </span>
              </div>
              <span title={`Each step on the Ladder is 1,375m. 4 steps = Min Goal (5.5k). 8 steps = Growth (11k). 12 steps = Legend (16.5k).`}>
                <strong style={{ color: stats.monthlyMinutes >= 11000 ? '#FCD34D' : (stats.monthlyMinutes >= 5500 ? '#C084FC' : '#60A5FA') }}>
                  {milestoneLabels[currentIdx]}
                </strong> ({Math.round(stats.monthlyMinutes % 1375)}m / 1375m)
              </span>
            </div>
            <div 
              title="Weekly Ladder: This bar fills up every 1375m. It's your current sprint target."
              onMouseEnter={(e) =>
                showProgressBarTooltip(e, {
                  icon: '🪜',
                  heading: 'WEEKLY LADDER',
                  color: stats.monthlyMinutes >= 11000 ? '#fcd34d' : (stats.monthlyMinutes >= 5500 ? '#a855f7' : '#ef4444'),
                  body: `Step: ${currentIdx + 1}/12.\n` +
                    `This sprint: ${Math.round(stats.monthlyMinutes % 1375)}m / 1375m.\n` +
                    `Current tier: ${milestoneLabels[currentIdx]}.`
                })
              }
              onMouseLeave={hideMetricTooltip}
              style={{ height: '8px', background: 'rgba(0,0,0,0.5)', borderRadius: '4px', overflow: 'hidden', position: 'relative', cursor: 'help' }}>
              <div style={{ 
                position: 'absolute', left: 0, top: 0, bottom: 0, 
                width: `${((stats.monthlyMinutes % 1375) / 1375) * 100}%`, 
                background: stats.monthlyMinutes >= 11000 ? '#fcd34d' : (stats.monthlyMinutes >= 5500 ? '#a855f7' : '#ef4444'),
                boxShadow: `0 0 10px ${stats.monthlyMinutes >= 11000 ? 'rgba(251,191,36,0.4)' : (stats.monthlyMinutes >= 5500 ? 'rgba(139,92,246,0.4)' : 'rgba(239,68,68,0.4)')}`,
                transition: 'width 0.5s ease-out',
                zIndex: 2
              }} />
              
              {/* 500min Nudges (8h shifts) overlay for the Step Bar */}
              <div style={{ position: 'absolute', inset: 0, display: 'flex', pointerEvents: 'none', zIndex: 5 }}>
                {Array.from({ length: 7 }).map((_, i) => {
                   // Map 7 segments to the current week
                   const dayOfStep = Math.floor(currentDay / 7) * 7 + i;
                   if (dayOfStep > daysInMonth) return null;
                   const dateObj = new Date(year, month, dayOfStep);
                   const dStr = dateObj.toDateString();
                   const isToday = dayOfStep === currentDay;

                   return (
                     <div key={i} style={{ flex: 1, position: 'relative', borderRight: '1px solid rgba(255,255,255,0.15)', overflow: 'hidden' }}>
                        <MiniDayTimeline 
                           dateStr={dStr} 
                           currentTimeline={isToday ? dailyTimeline : null}
                           dailyMins={isToday ? totalDailyMins : (dailyLog[dStr] || 0)}
                           goalMins={dailyGoal}
                        />
                     </div>
                   );
                })}
              </div>
            </div>
          </div>

          {/* Daily bar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: 'var(--text-muted)', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                <span title="Shift starts at 9:00 AM">☀️ 09:00 (Min: {dailyGoal}m)</span>
                {!isActive && (
                  <span
                    style={{ color: '#fdba74', fontWeight: 900 }}
                    title="Off-call elapsed today (avail + breaks)"
                  >
                    🚪 {formatTime(Math.floor(totalOffCallSeconds))}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                {stats.dailyMinutes >= 480 ? (
                  <span style={{ color: '#fcd34d', fontWeight: 800 }}>👑 LEGENDARY DAY (480m+)</span>
                ) : stats.dailyMinutes >= 350 ? (
                  <span style={{ color: '#c084fc', fontWeight: 800 }}>🚀 GROWTH DAY (350m+)</span>
                ) : stats.dailyMinutes >= dailyGoal ? (
                  <span style={{ color: '#34d399', fontWeight: 800 }}>🎉 SHIFT MET ({dailyGoal}m)</span>
                ) : (
                  <>
                    <span title={`SHIFT REMAINING: ${Math.max(0, DAILY_SHIFT_END - currentTime).toFixed(1)} hours left until 18:00.`}>⏳ {Math.max(0, DAILY_SHIFT_END - currentTime).toFixed(1)}h left</span>
                    <span title={`ESTIMATED YIELD: Based on your current rate, you can realistically bank another ${Math.round(Math.max(0, DAILY_SHIFT_END - currentTime) * 35)}m today, worth AR$${Math.round(Math.max(0, DAILY_SHIFT_END - currentTime) * 35 * RATE_PER_MINUTE * arsRate).toLocaleString('es-AR')}.`}>({Math.round(Math.max(0, DAILY_SHIFT_END - currentTime) * 35)}m / AR$${Math.round(Math.max(0, DAILY_SHIFT_END - currentTime) * 35 * RATE_PER_MINUTE * arsRate).toLocaleString('es-AR')})</span>
                  </>
                )}
                <button
                  onClick={() => setOvertimeMode(m => m === 'tail' ? 'under' : 'tail')}
                  title={`Overtime display: ${overtimeMode === 'tail' ? 'Extended tail' : 'Micro under-bar'}. Click to toggle.`}
                  style={{ fontSize: '0.5rem', padding: '1px 4px', borderRadius: '3px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(139,92,246,0.2)', color: '#c4b5fd', cursor: 'pointer', fontWeight: 700 }}
                >
                  {overtimeMode === 'tail' ? '➡️ TAIL' : '⬇️ UNDER'}
                </button>
              </div>
              <span title="Shift ends at 6:00 PM">🌙 18:00 (Focus: 480m)</span>
            </div>
            <div 
              title="Daily Multi-Tier Bar: Blue (Floor), Purple (350m Growth), Gold (480m focus)."
              onMouseEnter={(e) =>
                showProgressBarTooltip(e, {
                  icon: '☀️',
                  heading: 'DAILY PROGRESS',
                  color: stats.dailyMinutes >= 480 ? '#fcd34d' : (stats.dailyMinutes >= 350 ? '#c084fc' : '#f87171'),
                  body: `Banked today: ${Math.round(stats.dailyMinutes)}m.\n` +
                    `Including current call: ${Math.round(totalDailyMins)}m / 480m.\n` +
                    `Min target: ${Math.round(dailyGoal)}m.\n` +
                    `Off-call elapsed today: ${formatValue(totalOffCallMins)}`
                })
              }
              onMouseLeave={hideMetricTooltip}
              style={{ height: '6px', background: 'rgba(251, 146, 60, 0.1)', borderRadius: '3px', position: 'relative', overflow: overtimeMode === 'tail' ? 'visible' : 'hidden', cursor: 'help' }}>
              
              {/* Target Notches */}
              <div style={{ position: 'absolute', inset: 0, display: 'flex', pointerEvents: 'none', zIndex: 4 }}>
                {[dailyGoal, 350].map(m => (
                  <div key={m} style={{ position: 'absolute', left: `${(m / 480) * 100}%`, top: 0, bottom: 0, width: '1px', background: 'rgba(255,255,255,0.3)' }} />
                ))}
              </div>

              {/* Chronological Timeline Segments */}
              <div style={{ position: 'absolute', inset: 0, zIndex: 6, pointerEvents: 'auto' }}>
                {(() => {
                  const items = [];
                  const shiftEndMs = dailyShiftEndMs;
                  
                  dailyTimeline.forEach((evt, idx) => {
                    const start = evt.start;
                    const end = evt.end || Date.now();
                    const isHovered = hoveredTimelineEvent?.idx === idx;
                    
                    const renderSeg = (key, sPos, ePos, isOvertime) => (
                      <div 
                        key={key}
                        className={`timeline-segment ${evt.type} ${!evt.end ? 'ongoing' : ''} ${isOvertime ? 'overtime' : ''}`}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setHoveredTimelineEvent({ ...evt, idx, left: rect.left + rect.width/2 });
                        }}
                        onMouseLeave={() => setHoveredTimelineEvent(null)}
                        style={{ 
                          left: `${Math.max(0, sPos)}%`, 
                          width: `${Math.max(0.5, ePos - Math.max(0, sPos))}%`,
                          zIndex: evt.type === 'work' ? 10 : (evt.type === 'break' ? 9 : 8),
                          cursor: 'crosshair',
                          opacity: hoveredTimelineEvent && !isHovered ? 0.3 : 1,
                          transition: 'opacity 0.2s ease'
                        }} 
                      />
                    );
                    
                    if (overtimeMode === 'tail') {
                      if (end <= shiftEndMs || start >= shiftEndMs) {
                        const startPos = getDailyTimelinePos(start);
                        const endPos = getDailyTimelinePos(end);
                        items.push(renderSeg(idx, startPos, endPos, start >= shiftEndMs));
                      } else {
                        const startPos1 = getDailyTimelinePos(start);
                        items.push(renderSeg(idx, startPos1, 100, false));
                        items.push(renderSeg(`${idx}-ot`, 100, getDailyTimelinePos(end), true));
                      }
                    } else {
                      // under mode: main bar only shows shift time
                      if (start < shiftEndMs) {
                        const startPos = getDailyTimelinePos(start);
                        const endPos = Math.min(100, getDailyTimelinePos(end));
                        items.push(renderSeg(idx, startPos, endPos, false));
                      }
                    }
                  });
                  
                  return items;
                })()}

                {/* Popover */}
                {hoveredTimelineEvent && (
                  <div style={{
                    position: 'fixed',
                    left: `${hoveredTimelineEvent.left}px`,
                    bottom: 'calc(100% - 10px)',
                    transform: 'translateX(-50%) translateY(-20px)',
                    background: 'rgba(15, 23, 42, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${hoveredTimelineEvent.type === 'work' ? '#f87171' : '#fb923c'}`,
                    padding: '0.4rem 0.6rem',
                    borderRadius: '8px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.5)',
                    zIndex: 1000,
                    pointerEvents: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    animation: 'slideUpBounce 0.2s cubic-bezier(0.17, 0.88, 0.32, 1.28) forwards'
                  }}>
                    <div style={{ fontSize: '0.6rem', textTransform: 'uppercase', fontWeight: 900, color: hoveredTimelineEvent.type === 'work' ? '#f87171' : '#fb923c', letterSpacing: '0.05em' }}>
                      {hoveredTimelineEvent.type} PERIOD
                    </div>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' }}>
                      {new Date(hoveredTimelineEvent.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} 
                      <span style={{ margin: '0 4px', opacity: 0.5 }}>→</span>
                      {hoveredTimelineEvent.end ? new Date(hoveredTimelineEvent.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'NOW'}
                    </div>
                    <div style={{ fontSize: '0.55rem', opacity: 0.6 }}>
                      Duration: {Math.round(((hoveredTimelineEvent.end || Date.now()) - hoveredTimelineEvent.start) / 60000)}m
                    </div>
                  </div>
                )}

                {/* Fill Avail gaps proactively */}
                {(() => {
                  const items = [];
                  if (dailyTimeline.length === 0 && stats.dayStartTime) {
                    const startPos = Math.max(0, getDailyTimelinePos(stats.dayStartTime));
                    const nowPos = getDailyTimelinePos(Date.now());
                    const endPos = overtimeMode === 'under' ? Math.min(100, nowPos) : nowPos;
                    items.push(<div key="init-avail" className="timeline-segment avail ongoing" style={{ left: `${startPos}%`, width: `${Math.max(0, endPos - startPos)}%` }} />);
                  }
                  return items;
                })()}
              </div>

              {/* Progress Fill (Unbanked) */}
              <div 
                title={`Unbanked Progress: You have ${formatTime(sessionSeconds)} in the current call.`}
                style={{ 
                  position: 'absolute', left: 0, top: 0, bottom: 0, 
                  width: `${Math.min(1, (stats.dailyMinutes + unbankedMins) / 480) * 100}%`, 
                  backgroundColor: '#f97316', 
                  opacity: 0.3, transition: 'width 1s linear', zIndex: 1, 
                  pointerEvents: 'none' 
                }} />
              
              {/* Progress Fill (Total Mins Goal Overlay) */}
              <div 
                title={`Daily Total (banked + current call): ${Math.round(totalDailyMins)}m`}
                style={{ 
                  position: 'absolute', left: 0, top: 0, bottom: 0, 
                  width: `${Math.min(1, totalDailyMins / 480) * 100}%`, 
                  backgroundColor: 'rgba(255,255,255,0.05)', 
                  transition: 'width 1s cubic-bezier(0.34, 1.56, 0.64, 1)', zIndex: 2, pointerEvents: 'none' 
                }} />
              
              {/* Hour Notches overlay */}
              <div 
                title={`Progress: ${liveDailyArs.toLocaleString('es-AR')} / ${dailyTargetArs.toLocaleString('es-AR')} ARS. Each notch = 1 hour of shift (9 AM - 6 PM).`}
                style={{ position: 'absolute', inset: 0, display: 'flex', pointerEvents: 'auto', zIndex: 11, cursor: 'help' }}>

                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} style={{ flex: 1, borderRight: i < 8 ? '1px solid rgba(255,255,255,0.15)' : 'none' }} />
                ))}
              </div>

              <div 
                title="Current Time indicator. Keep the daily bar touching or ahead of this line."
                style={{ position: 'absolute', top: 0, bottom: 0, left: `${shiftElapsedRatio * 100}%`, width: '2px', backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10, cursor: 'help', pointerEvents: 'auto' }} />
            </div>
            
            {/* Overtime under-bar */}
            {overtimeMode === 'under' && (
              <div style={{ height: '3px', background: 'rgba(139, 92, 246, 0.15)', borderRadius: '1px', position: 'relative', overflow: 'hidden', marginTop: '1px' }}>
                {dailyTimeline.map((evt, idx) => {
                  const start = evt.start;
                  const end = evt.end || Date.now();
                  if (end <= dailyShiftEndMs) return null;
                  const startPos = start < dailyShiftEndMs ? 0 : getOvertimePos(start);
                  const endPos = getOvertimePos(end);
                  const isHovered = hoveredTimelineEvent?.idx === idx;
                  return (
                    <div 
                      key={`ot-${idx}`}
                      className={`timeline-segment ${evt.type} ${!evt.end ? 'ongoing' : ''} overtime`}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setHoveredTimelineEvent({ ...evt, idx, left: rect.left + rect.width/2 });
                      }}
                      onMouseLeave={() => setHoveredTimelineEvent(null)}
                      style={{ 
                        left: `${startPos}%`, 
                        width: `${Math.max(0.5, endPos - startPos)}%`,
                        zIndex: evt.type === 'work' ? 10 : (evt.type === 'break' ? 9 : 8),
                        cursor: 'crosshair',
                        opacity: hoveredTimelineEvent && !isHovered ? 0.3 : 1,
                        transition: 'opacity 0.2s ease'
                      }} 
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      </>
      </div>
  );

  return (
    <>
    {/* v4.133.0: the off-call 💵⏱☕ chip lives in the sticky row's own
        `.off-call-targets-row` (inside `.session-controls-center`) for EVERY
        off-call view; the audio chips row trailing slot carries only the
        scoreboard inline metrics strip. 3884ce5 pushed the chip into that
        trailing slot — it then measured the whole audio line and overlapped
        the TAB/VB proof. Chip never returns to `trailingSlot`. */}
    <header className={`dashboard-header glass-panel${headerMinimal ? ' dashboard-header--minimal' : ''}${headerCallCompact ? ' dashboard-header--call-compact' : ''}${isActive && callModeExpanded ? ' dashboard-header--call-expanded' : ''}${offCallScoreboardView ? ' dashboard-header--off-call-scoreboard' : ''}${offCallScoreboardView && offCallMetricsExpanded ? ' dashboard-header--metrics-expanded' : ''}${meterOnlyMode ? ' dashboard-header--meter-only' : ''}`}
      // v4.164.0: the grab-bar height used to land here as an inline maxHeight,
      // which silently beat every CSS cap (index.css:372, 1145, 1154, 1163, 4232)
      // and made the vertical budget unreadable in the stylesheet. It is a custom
      // property now; the rule that consumes it lives at index.css:1163.
      style={{ position: 'relative', zIndex: 100, '--scoreboard-max-vh': String(scoreboardMaxVh) }}>
      <SessionControlsSticky
        isActive={isActive}
        ioStripOn={ioStripOn}
        onToggleIoStrip={toggleIoStrip}
        gridInCall={gridInCall}
        onToggleGridInCall={toggleGridInCall}
        isBreakActive={isBreakActive}
        isZombieCall={isZombieCall}
        onCallMins={totalDailyMins}
        offCallMins={totalOffCallMins}
        dailyIncomeArs={todayArsLive}
        apiKeyMissing={apiKeyMissing}
        vaultNeedsDecrypt={vaultNeedsDecrypt}
        apiKeyMissingNoVault={apiKeyMissingNoVault}
        audioAttached={audioAttached}
        micTestMode={micTestMode}
        setMicTestMode={setMicTestMode}
        connectionState={connectionState}
        connectionMessage={connectionMessage}
        connectProgress={connectProgress}
        apiKeyRejected={apiKeyRejected}
        settingsOpen={settingsOpen}
        vaultStatus={vaultStatus}

        showEndDayButton={showEndDayButton}
        onEndDay={handleEndDay}

        connectFlash={connectFlash}
        connectLabel={connectLabel}
        connectSingleTitle={connectSingleTitle}
        connectDoubleTitle={connectDoubleTitle}
        connectOnSingle={connectOnSingle}
        connectOnDouble={connectOnDouble}
        requireDoubleTapIndicator={connectRequireDoubleTapIndicator}
        pendingDoubleTapTitle="Tap again to pick tab"
        onArmDoubleTap={() => audioEngine.playWarningPing?.()}

        minutesSinceLastBreak={minutesSinceLastBreak}
        shouldBreakNudge={shouldBreakNudge}
        stopBreak={stopBreak}
        onStartBreak={handleStartBreak}

        handleStop={handleStop}
        isHold={isHold}
        setIsHold={setIsHold}
        holdSeconds={holdSeconds}
        disableZap={false}
        isZapping={isZapping}
        onReconnectStream={handleZap}
        sttStall={sttStall}

        silenceCount={silenceCount}
        lastEnglishActivityTime={lastEnglishActivityTime}
        sessionSeconds={sessionSeconds}
        sessionArsLive={sessionArsLive}
        totalOffCallSeconds={totalOffCallSeconds}
        totalOnCallSeconds={totalOnCallSeconds}
        lastCallSeconds={lastCallSeconds}
        lastCallEndedAt={lastCallEndedAt}
        availSeconds={availSeconds}
        dailyMinutes={Math.round(totalDailyMins)}
        monthlyMinutes={monthlyBanked}
        workDays={goalWorkDays}
        breakMinutes={Math.round(liveBreakMins)}
        ratePerMinute={RATE_PER_MINUTE}
        goalMinutes={stats.goalMinutes}
        goalSetAt={stats.goalSetAt || null}
        goalBaseMinutes={stats.goalBaseMinutes || 0}
        callModeExpanded={callModeExpanded}
        setCallModeExpanded={setCallModeExpanded}

        tabStreamReady={tabStreamReady}
        cableStreamReady={cableStreamReady}
        lastDataTime={lastDataTime}
        onOpenSoundboard={onOpenSoundboard}
        soundboardOpen={soundboardOpen}
        onOpenGoalsView={onOpenGoalsView}
        goalsOpen={goalsOpen}
        sttLanguage={sttLanguage}
        configuredAudioSourceMode={configuredAudioSourceMode}
        attachedAudioSourceMode={attachedAudioSourceMode}
        virtualCableFailure={virtualCableFailure}
        onReconnectAudioSource={onReconnectAudioSource}
        onSwitchToTabShare={onSwitchToTabShare}
        onSwitchToVirtualCable={onSwitchToVirtualCable}
        onOpenLanguageSettings={() => {
          try {
            window.dispatchEvent(new CustomEvent('cat_show_language_settings'));
          } catch (_) {}
        }}
        languagePairLabel={languagePairLabel}
        offCallStatusLabel={offCallStatusLabel}
        meterOnly={meterOnlyMode}
        onToggleMeterHud={toggleMeterHud}
        trailingSlot={offCallScoreboardView ? renderInlineMetricsStrip(offCallMetricsExpanded) : null}
      />

      {headerCallCompact && (
        <div
          id="compact-call-goal-meter"
          className={`compact-call-goal-meter${meterOnlyMode ? ' compact-call-goal-meter--big' : ''}`}
          title={`Workday: ${formatTime(totalOnCallSeconds)} on call; ${formatTime(totalOffCallSeconds)} off call; ${formatTime(compactShiftRemainingSeconds)} left. Blue = calls; orange = available; red = break.`}
          aria-label={`Workday timeline: ${formatTime(totalOnCallSeconds)} on call, ${formatTime(totalOffCallSeconds)} off call, and ${formatTime(compactShiftRemainingSeconds)} left.`}
        >
          <span className="compact-call-goal-meter__readout" aria-hidden="true">
            <b>ON {formatTime(totalOnCallSeconds)}</b><span>OFF {formatTime(totalOffCallSeconds)}</span><span>LEFT {formatTime(compactShiftRemainingSeconds)}</span>
          </span>
          <div className="compact-call-goal-meter__track" aria-hidden="true">
            {compactWorkdaySegments.map((segment) => (
              <span
                key={`${segment.type}-${segment.index}`}
                className={`compact-call-goal-meter__segment compact-call-goal-meter__segment--${segment.type}`}
                style={{ left: `${segment.left}%`, width: `${segment.width}%` }}
              />
            ))}
            <span className="compact-call-goal-meter__now" style={{ left: `${shiftElapsedRatio * 100}%` }} />
          </div>
          {/* v4.88.5: meter-only HUD carries the daily targets beside the timeline */}
          {meterOnlyMode && (
            renderTargetsChip()
          )}
        </div>
      )}

      {!headerMinimal && (
        headerCallCompact
          ? (gridInCall ? <div className="header-call-grid">{renderNumericGrid()}</div> : null)
          : offCallScoreboardView && !offCallMetricsExpanded
            ? renderOffCallCollapsedBody()
            : renderWorkspaceBody()
      )}

      {/* Grab bar: drag to resize scoreboard height (v4.86.5) */}
      {offCallScoreboardView && offCallMetricsExpanded && (
        <div
          className="scoreboard-grab-bar"
          role="separator"
          aria-orientation="horizontal"
          aria-label="Drag to resize scoreboard height"
          title="Drag to resize scoreboard height (stays per browser)"
          onMouseDown={startScoreboardResize}
        >
          <span className="scoreboard-grab-bar-grip" />
        </div>
      )}

      {timeEditMode && <TimeEditModal mode={timeEditMode} onClose={() => setTimeEditMode(null)} />}

      {celebration && (
        <CelebrationParticles
          {...celebration}
          onDismiss={() => setCelebration(null)}
        />
      )}
    </header>

    </>
  );
};
